import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Users, Calendar, Image, BarChart3, Settings, LogOut, Menu, X, 
  Plus, Search, Phone, User, ChevronLeft, Trash2, Edit, Clock,
  Activity, TrendingUp, Bell, Camera, FileText, Home, DollarSign,
  Download, Wallet, CreditCard, ArrowUpCircle, ArrowDownCircle, Printer,
  FileSpreadsheet, Archive, CheckCircle, Gift, Filter, StickyNote, Database,
  Cake, AlertCircle, Eye, KeyRound, Edit2, Check, Globe, Bot, UserCheck, Tag,
  Share2, Instagram
} from "lucide-react";
import { Package } from "lucide-react";

// Imported Contexts
import { AuthProvider, useAuth } from "./context/AuthContext";

// Imported Pages
import { DashboardPage, NotificationsPage, SessionsPage, AppointmentsPage, StaffStatsPage, ConsultationsPage, DebtsPage, ReportsPage } from "./pages";
import WebsitePage from "./pages/WebsitePage";
import WebsiteManagePage from "./pages/WebsiteManagePage";
import AIDashboardPage from "./pages/AIDashboardPage";
import LeadsPage from "./pages/LeadsPage";
import OffersReviewsPage from "./pages/OffersReviewsPage";
import ReviewPage from "./pages/ReviewPage";
import InstagramManagePage from "./pages/InstagramManagePage";
import PayrollPage from "./pages/PayrollPage";
import AIChatPage from "./pages/AIChatPage";
import AIFeaturesPage from "./pages/AIFeaturesPage";
import AdvancedAccountingPage from "./pages/AdvancedAccountingPage";
import FollowUpPage from "./pages/FollowUpPage";
import InventoryPage from "./pages/InventoryPage";
import EmployeeDetailPage from "./pages/EmployeeDetailPage";

// Imported Utils
import { api } from "./utils/api";
import { formatNumber, calculateAge, formatDateArabic, formatFullDateTime, formatDateTime } from "./utils/helpers";
import { escapeHtml, openPrintWindow } from "./utils/sanitize";
import { loadXLSX, loadJsPDF, loadJSZip, loadFileSaver, loadDocx } from "./utils/lazyLibs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${BACKEND_URL}${url}`;
};

// Login Page
const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ name: "", username: "", password: "", role: "staff" });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Clear any old cached data before login
    localStorage.removeItem("token");
    try {
      await login(formData.username, formData.password);
      toast.success("تم تسجيل الدخول بنجاح");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في اسم المستخدم أو كلمة السر");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card animate-fade-in">
        <div className="login-logo">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center">
            <Activity className="w-8 h-8 text-white" />
          </div>
          <h1>عيادة زراعة الشعر</h1>
          <p>نظام إدارة المرضى</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">اسم المستخدم</label>
            <input
              type="text"
              className="form-input"
              placeholder="ادخل اسم المستخدم"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              data-testid="username-input"
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label className="form-label">كلمة المرور</label>
            <input
              type="password"
              className="form-input"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              data-testid="password-input"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary w-full mt-6"
            disabled={loading}
            data-testid="submit-btn"
          >
            {loading ? "جاري التحميل..." : "تسجيل الدخول"}
          </button>
        </form>

        <p className="text-center mt-6 text-gray-500 text-xs">
          للحصول على حساب، تواصل مع مدير النظام
        </p>
        
        <Link to="/reset-password" className="block text-center mt-4 text-blue-500 hover:text-blue-600 text-sm">
          نسيت كلمة المرور؟
        </Link>
      </div>
    </div>
  );
};

// Password Reset Page
const PasswordResetPage = () => {
  const [formData, setFormData] = useState({ username: "", newPassword: "", secretKey: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/reset-password-direct`, {
        username: formData.username,
        new_password: formData.newPassword,
        secret_key: formData.secretKey
      });
      toast.success("تم تغيير كلمة المرور بنجاح!");
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "حدث خطأ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card animate-fade-in">
        <div className="login-logo">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
            <KeyRound className="w-8 h-8 text-white" />
          </div>
          <h1>إعادة تعيين كلمة المرور</h1>
          <p>أدخل بيانات حسابك ومفتاح الأمان</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">اسم المستخدم</label>
            <input
              type="text"
              className="form-input"
              placeholder="ادخل اسم المستخدم"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">كلمة المرور الجديدة</label>
            <input
              type="password"
              className="form-input"
              placeholder="••••••••"
              value={formData.newPassword}
              onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">مفتاح الأمان</label>
            <input
              type="password"
              className="form-input"
              placeholder="أدخل مفتاح الأمان"
              value={formData.secretKey}
              onChange={(e) => setFormData({ ...formData, secretKey: e.target.value })}
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary w-full mt-6"
            disabled={loading}
          >
            {loading ? "جاري التحميل..." : "تغيير كلمة المرور"}
          </button>
        </form>

        <Link to="/login" className="block text-center mt-6 text-blue-500 hover:text-blue-600 text-sm">
          العودة لتسجيل الدخول
        </Link>
      </div>
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, isAdmin, isAdminOrAccountant, permissions } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const admin = isAdmin();

  // Define nav items with permission-based visibility
  const allNavItems = [
    { path: "/dashboard", icon: Home, label: "الرئيسية", everyone: true },
    { path: "/patients", icon: Users, label: "المرضى", everyone: true },
    { path: "/accounting", icon: Wallet, label: "المحاسبة", show: permissions.accounting },
    { path: "/payroll", icon: DollarSign, label: "الرواتب", show: permissions.payroll },
    { path: "/debts", icon: CreditCard, label: "الديون", show: permissions.debts },
    { path: "/notifications", icon: Bell, label: "التنبيهات", everyone: true },
    { path: "/followup", icon: UserCheck, label: "متابعة العمليات", everyone: true },
    { path: "/sessions", icon: FileText, label: "الجلسات", everyone: true },
    { path: "/inventory", icon: Package, label: "المخزون", everyone: true },
    { path: "/settings", icon: Settings, label: "الإعدادات", show: permissions.settings },
    { path: "/website-manage", icon: Globe, label: "إدارة الموقع", show: permissions.website },
    { path: "/ai-chat", icon: Bot, label: "المساعد الذكي", show: permissions.ai },
    { path: "/ai-features", icon: Bot, label: "أدوات AI", show: permissions.ai },
  ];

  const navItems = allNavItems.filter(item => item.everyone || item.show);

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success("تم تسجيل الخروج");
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={onClose} />
      )}
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-sm leading-tight">المركز الوطني التركي</h2>
              <p className="text-xs text-gray-500">لزراعة الشعر</p>
            </div>
          </div>
          <button onClick={onClose} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <div
              key={item.path}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => { navigate(item.path); onClose(); }}
              data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
            >
              <item.icon className="rtl-flip" />
              <span>{item.label}</span>
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-200 space-y-2">
          <div
            className="nav-item text-blue-500 hover:bg-blue-50"
            onClick={() => {
              // Clear all caches
              if ('caches' in window) {
                caches.keys().then(names => names.forEach(name => caches.delete(name)));
              }
              // Unregister service workers
              if ('serviceWorker' in navigator) {
                navigator.serviceWorker.getRegistrations().then(registrations => {
                  registrations.forEach(reg => reg.unregister());
                });
              }
              // Clear local storage except token
              const token = localStorage.getItem('token');
              localStorage.clear();
              if (token) localStorage.setItem('token', token);
              // Reload
              window.location.reload(true);
              toast.success("تم تحديث التطبيق");
            }}
            data-testid="refresh-btn"
          >
            <Activity className="rtl-flip" />
            <span>تحديث التطبيق</span>
          </div>
          <div
            className="nav-item text-red-500 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="rtl-flip" />
            <span>تسجيل الخروج</span>
          </div>
        </div>
      </aside>
    </>
  );
};

// Patients Page
const PatientsPage = () => {
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [patients, setPatients] = useState([]);
  const [allPatients, setAllPatients] = useState([]); // لربط المرضى ببعض
  const [search, setSearch] = useState("");
  const [filterFollowup, setFilterFollowup] = useState(false);
  const [filterType, setFilterType] = useState("all"); // all, center, hall_rental
  const [filterTransplantType, setFilterTransplantType] = useState("all"); // all, hair, beard, eyebrow
  const [filterFinancial, setFilterFinancial] = useState("all"); // all, paid, pending, unpaid
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [sortBy, setSortBy] = useState("operation_date"); // operation_date, created_at, name
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(location.search.includes("new=true"));
  const [pageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingPatient, setEditingPatient] = useState(null);
  const [activeFormTab, setActiveFormTab] = useState("personal");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  const initialFormData = {
    // المعلومات الشخصية
    name: "", last_name: "", phone: "", dob: "", gender: "male", occupation: "",
    address: "",
    // المعلومات الطبية
    blood_type: "", previous_surgery: "", previous_surgery_details: "",
    has_allergies: "", allergies: "", has_medications: "", medications: "",
    smoking: "", alcohol: "", blood_pressure: "", diabetes: "",
    hair_loss_cause: "", medical_history: "",
    // معلومات المركز والعملية
    patient_type: "", hall_rental_from: "",
    transplant_type: "", // شعر/لحية/حاجب
    session_type_first_or_second: "", // جلسة أولى أم ثانية
    extraction_by: "", extraction_by_other: "",
    channel_opening_by: "", channel_opening_by_other: "",
    grafts_count: "", hair_type: "",
    transplant_date: "", transplant_1: "", transplant_2: "",
    // الباقة المشمولة
    package_plasma_sessions: "", package_meso_sessions: "",
    shampoo_delivered: false, lotion_delivered: false, vitamins_delivered: false,
    // حالة الجلسات (للمرضى القدام)
    completed_sessions_before: "", sessions_status: "",
    // معلومات الحجز والتسعير
    referred_by_patient_id: "", referral_source: "", booking_source: "",
    data_entry_by: "", // مدخل البيانات
    price: "", total_paid: "", remaining_amount: "", currency: "IQD",
    // نسب الكادر
    extraction_cost: "", channels_cost: "", planting_cost: "",
    // الأقساط
    payment_type: "cash", installment_company: "", installment_company_other: "",
    installment_amount: "", installment_percent: "15",
    installment_status: "pending", installment_expected_date: "",
    installment_received_date: "",
    // المواعيد
    operation_date: "", next_followup_date: "",
    notes: ""
  };
  
  const [formData, setFormData] = useState(initialFormData);
  
  // Ref for modal body scroll
  const modalBodyRef = useRef(null);

  // Auto scroll to top when modal opens from quick button
  useEffect(() => {
    if (location.search.includes("new=true") && showModal) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // Handle edit parameter - only if modal is not already open
    if (location.search.includes("edit=") && !showModal) {
      const editId = new URLSearchParams(location.search).get("edit");
      if (editId && patients.length > 0) {
        const patientToEdit = patients.find(p => p.id === editId);
        if (patientToEdit) {
          openEditModal(patientToEdit, { stopPropagation: () => {} });
        }
      }
    }
  }, [location.search, patients]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get staff members from localStorage
  const [staffMembers, setStaffMembers] = useState({
    extraction: [],
    channels: [],
    planting: [],
    dataEntry: []
  });

  // Load staff from database
  useEffect(() => {
    const loadStaffFromDB = async () => {
      try {
        const res = await api.get("/clinic-settings", token);
        if (res.data?.staff) {
          setStaffMembers(res.data.staff);
        } else {
          // Fallback to localStorage
          const saved = localStorage.getItem("clinic_staff");
          if (saved) setStaffMembers(JSON.parse(saved));
        }
      } catch (error) {
        // Fallback to localStorage
        const saved = localStorage.getItem("clinic_staff");
        if (saved) setStaffMembers(JSON.parse(saved));
      }
    };
    if (token) loadStaffFromDB();
  }, [token]);

  const fetchPatients = async () => {
    try {
      let url = "/patients";
      const params = [];
      if (search) params.push(`search=${search}`);
      if (filterFollowup) params.push("upcoming_followup=true");
      if (params.length > 0) url += `?${params.join("&")}`;
      
      const response = await api.get(url, token);
      let filteredPatients = response.data;
      
      // Apply advanced filters
      // Financial filter
      if (filterFinancial !== "all") {
        filteredPatients = filteredPatients.filter(p => {
          const remaining = (p.remaining_amount || 0);
          const paid = (p.total_paid || 0);
          const price = (p.price || 0);
          
          if (filterFinancial === "paid") return remaining === 0 && paid > 0;
          if (filterFinancial === "pending") return remaining > 0 && paid > 0;
          if (filterFinancial === "unpaid") return paid === 0 && price > 0;
          return true;
        });
      }
      
      // Date range filter (operation_date)
      if (filterDateFrom) {
        filteredPatients = filteredPatients.filter(p => 
          p.operation_date && p.operation_date >= filterDateFrom
        );
      }
      if (filterDateTo) {
        filteredPatients = filteredPatients.filter(p => 
          p.operation_date && p.operation_date <= filterDateTo
        );
      }
      
      // Sort patients
      const sortedPatients = filteredPatients.sort((a, b) => {
        if (sortBy === "operation_date") {
          // Sort by operation date (newest first), nulls at end
          const dateA = a.operation_date || "0000-00-00";
          const dateB = b.operation_date || "0000-00-00";
          return dateB.localeCompare(dateA);
        } else if (sortBy === "created_at") {
          return new Date(b.created_at) - new Date(a.created_at);
        } else if (sortBy === "name") {
          return (a.name || "").localeCompare(b.name || "", 'ar');
        }
        return 0;
      });
      
      setPatients(sortedPatients);
      setAllPatients(response.data); // Keep all for referral dropdown
    } catch (error) {
      toast.error("خطأ في تحميل المرضى");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, search, filterFollowup, filterFinancial, filterDateFrom, filterDateTo, sortBy]);

  // Scroll modal to top when opened
  useEffect(() => {
    if (showModal && modalBodyRef.current) {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        if (modalBodyRef.current) {
          modalBodyRef.current.scrollTop = 0;
        }
      }, 0);
    }
  }, [showModal]);

  // Filter by patient type and transplant type
  const filteredPatients = patients.filter(p => {
    const typeMatch = filterType === "all" || p.patient_type === filterType;
    const transplantMatch = filterTransplantType === "all" || p.transplant_type === filterTransplantType;
    return typeMatch && transplantMatch;
  });

  const totalPages = Math.ceil(filteredPatients.length / pageSize);
  const paginatedPatients = filteredPatients.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [search, filterFollowup, filterFinancial, filterType, filterTransplantType, filterDateFrom, filterDateTo]);

  // Reset form and scroll when opening new patient modal
  const openNewPatientModal = () => {
    setEditingPatient(null);
    setFormData(initialFormData);
    setActiveFormTab("personal");
    setShowModal(true);
    // Scroll page to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Scroll modal body to top after state update
    setTimeout(() => {
      if (modalBodyRef.current) {
        modalBodyRef.current.scrollTop = 0;
      }
    }, 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { 
        ...formData, 
        dob: formData.dob || null,
        grafts_count: formData.grafts_count ? parseInt(formData.grafts_count) : null,
        price: formData.price ? parseFloat(formData.price) : null,
        total_paid: formData.total_paid ? parseFloat(formData.total_paid) : null,
        remaining_amount: formData.remaining_amount ? parseFloat(formData.remaining_amount) : null,
        extraction_cost: formData.extraction_cost ? parseFloat(formData.extraction_cost) : null,
        channels_cost: formData.channels_cost ? parseFloat(formData.channels_cost) : null,
        planting_cost: formData.planting_cost ? parseFloat(formData.planting_cost) : null,
        package_plasma_sessions: formData.package_plasma_sessions ? parseInt(formData.package_plasma_sessions) : null,
        package_meso_sessions: formData.package_meso_sessions ? parseInt(formData.package_meso_sessions) : null,
        operation_date: formData.operation_date || null,
        next_followup_date: formData.next_followup_date || null,
        transplant_date: formData.transplant_date || null,
        referred_by_patient_id: formData.referred_by_patient_id || null,
        completed_sessions_before: formData.completed_sessions_before ? parseInt(formData.completed_sessions_before) : null,
        sessions_status: formData.sessions_status || null,
        payment_type: formData.payment_type || null,
        installment_company: formData.installment_company === "other" ? (formData.installment_company_other || "أخرى") : (formData.installment_company || null),
        installment_amount: formData.installment_amount ? parseFloat(formData.installment_amount) : null,
        installment_status: formData.installment_status || null,
        installment_expected_date: formData.installment_expected_date || null,
        installment_received_date: formData.installment_received_date || null
      };
      
      if (editingPatient) {
        await api.put(`/patients/${editingPatient.id}`, data, token);
        toast.success("تم تحديث بيانات المريض");
      } else {
        await api.post("/patients", data, token);
        toast.success("تم إضافة المريض بنجاح");
      }
      // Clear edit parameter from URL FIRST to prevent re-opening
      if (location.search.includes("edit=")) {
        navigate("/patients", { replace: true });
      }
      setShowModal(false);
      setEditingPatient(null);
      setFormData(initialFormData);
      setActiveFormTab("personal");
      fetchPatients();
    } catch (error) {
      toast.error("خطأ في حفظ بيانات المريض");
    }
  };

  const openEditModal = (patient, e) => {
    e.stopPropagation();
    setEditingPatient(patient);
    setFormData({
      name: patient.name || "",
      last_name: patient.last_name || "",
      phone: patient.phone || "",
      dob: patient.dob || "",
      gender: patient.gender || "male",
      occupation: patient.occupation || "",
      address: patient.address || "",
      blood_type: patient.blood_type || "",
      previous_surgery: patient.previous_surgery || "",
      previous_surgery_details: patient.previous_surgery_details || "",
      has_allergies: patient.has_allergies || "",
      allergies: patient.allergies || "",
      has_medications: patient.has_medications || "",
      medications: patient.medications || "",
      smoking: patient.smoking || "",
      alcohol: patient.alcohol || "",
      blood_pressure: patient.blood_pressure || "",
      diabetes: patient.diabetes || "",
      hair_loss_cause: patient.hair_loss_cause || "",
      medical_history: patient.medical_history || "",
      patient_type: patient.patient_type || "",
      hall_rental_from: patient.hall_rental_from || "",
      transplant_type: patient.transplant_type || "",
      session_type_first_or_second: patient.session_type_first_or_second || "",
      extraction_by: patient.extraction_by || "",
      extraction_by_other: patient.extraction_by_other || "",
      channel_opening_by: patient.channel_opening_by || "",
      channel_opening_by_other: patient.channel_opening_by_other || "",
      grafts_count: patient.grafts_count?.toString() || "",
      hair_type: patient.hair_type || "",
      transplant_date: patient.transplant_date || "",
      transplant_1: patient.transplant_1 || "",
      transplant_2: patient.transplant_2 || "",
      package_plasma_sessions: patient.package_plasma_sessions?.toString() || "",
      package_meso_sessions: patient.package_meso_sessions?.toString() || "",
      shampoo_delivered: patient.shampoo_delivered || false,
      lotion_delivered: patient.lotion_delivered || false,
      vitamins_delivered: patient.vitamins_delivered || false,
      completed_sessions_before: patient.completed_sessions_before?.toString() || "",
      sessions_status: patient.sessions_status || "",
      referred_by_patient_id: patient.referred_by_patient_id || "",
      referral_source: patient.referral_source || "",
      booking_source: patient.booking_source || "",
      data_entry_by: patient.data_entry_by || "",
      price: patient.price?.toString() || "",
      total_paid: patient.total_paid?.toString() || "",
      remaining_amount: patient.remaining_amount?.toString() || "",
      currency: patient.currency || "IQD",
      extraction_cost: patient.extraction_cost?.toString() || "",
      channels_cost: patient.channels_cost?.toString() || "",
      planting_cost: patient.planting_cost?.toString() || "",
      payment_type: patient.payment_type || "cash",
      installment_company: ["الرافدين"].includes(patient.installment_company) ? patient.installment_company : (patient.installment_company ? "other" : ""),
      installment_company_other: ["الرافدين"].includes(patient.installment_company) ? "" : (patient.installment_company || ""),
      installment_amount: patient.installment_amount?.toString() || "",
      installment_percent: patient.installment_amount && patient.price ? Math.round(patient.installment_amount / patient.price * 100).toString() : "15",
      installment_status: patient.installment_status || "pending",
      installment_expected_date: patient.installment_expected_date || "",
      installment_received_date: patient.installment_received_date || "",
      operation_date: patient.operation_date || "",
      next_followup_date: patient.next_followup_date || "",
      notes: patient.notes || ""
    });
    setShowModal(true);
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Export all patients to Excel
  const exportPatientsToExcel = async () => {
    if (filteredPatients.length === 0) {
      toast.error("لا يوجد مرضى للتصدير");
      return;
    }
    toast.info("جاري التحضير...");
    const XLSX = await loadXLSX();
    
    const excelData = filteredPatients.map(p => ({
      "الاسم": p.name,
      "اللقب": p.last_name || "",
      "الهاتف": p.phone,
      "تاريخ الميلاد": p.dob || "",
      "العمر": calculateAge(p.dob) || "",
      "الجنس": p.gender === "male" ? "ذكر" : "أنثى",
      "العنوان": p.address || "",
      "نوع المريض": p.patient_type === "center" ? "مركز" : p.patient_type === "hall_rental" ? "تأجير" : "",
      "تاريخ الزراعة": p.transplant_date || "",
      "عدد البصيلات": p.grafts_count || "",
      "الاقتطاف": p.extraction_by || "",
      "القنوات": p.channel_opening_by || "",
      "السعر": p.price || 0,
      "المدفوع": p.total_paid || 0,
      "المتبقي": p.remaining_amount || 0,
      "العملة": p.currency || "IQD",
      "عدد الجلسات": p.total_sessions || 0,
      "موعد العملية": p.operation_date || "",
      "موعد المراجعة": p.next_followup_date || "",
      "فصيلة الدم": p.blood_type || "",
      "تاريخ الإضافة": p.created_at?.split('T')[0] || ""
    }));
    
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "المرضى");
    const colWidths = Object.keys(excelData[0] || {}).map(() => ({ wch: 15 }));
    ws['!cols'] = colWidths;
    XLSX.writeFile(wb, `patients_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success(`تم تصدير ${filteredPatients.length} مريض بنجاح`);
  };

  // إنشاء ملف Word للمريض
  const createPatientWordDoc = async (patient, sessions) => {
    const { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType, BorderStyle } = await loadDocx();
    const rows = [];
    
    // معلومات المريض
    const infoRows = [
      ["الرقم الصحي", patient.medical_id || "-"],
      ["الاسم الكامل", `${patient.name} ${patient.last_name || ""}`],
      ["رقم الهاتف", patient.phone || "-"],
      ["تاريخ الميلاد", patient.dob || "-"],
      ["العمر", patient.dob ? `${calculateAge(patient.dob)} سنة` : "-"],
      ["الجنس", patient.gender === "male" ? "ذكر" : "أنثى"],
      ["العنوان", patient.address || "-"],
      ["الوظيفة", patient.occupation || "-"],
      ["فصيلة الدم", patient.blood_type || "-"],
      ["نوع الزراعة", patient.transplant_type === "hair" ? "شعر" : patient.transplant_type === "beard" ? "لحية" : patient.transplant_type === "eyebrow" ? "حاجب" : "-"],
      ["جلسة أولى/ثانية", patient.session_type_first_or_second === "first" ? "جلسة أولى" : patient.session_type_first_or_second === "second" ? "جلسة ثانية" : "-"],
      ["تاريخ الزراعة", patient.transplant_date || "-"],
      ["عدد البصيلات", patient.grafts_count || "-"],
      ["الاقتطاف", patient.extraction_by || "-"],
      ["فتح القنوات", patient.channel_opening_by || "-"],
      ["زراعة 1", patient.transplant_1 || "-"],
      ["زراعة 2", patient.transplant_2 || "-"],
      ["السعر", patient.price ? `${patient.price} ${patient.currency || "IQD"}` : "-"],
      ["المدفوع", patient.total_paid ? `${patient.total_paid}` : "0"],
      ["المتبقي", patient.remaining_amount ? `${patient.remaining_amount}` : "0"],
      ["مدخل البيانات", patient.data_entry_by || "-"],
      ["ملاحظات", patient.notes || "-"]
    ];

    const tableRows = infoRows.map(([label, value]) => 
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: label, bold: true, size: 24 })], alignment: AlignmentType.RIGHT })],
            width: { size: 30, type: WidthType.PERCENTAGE }
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: String(value), size: 24 })], alignment: AlignmentType.RIGHT })],
            width: { size: 70, type: WidthType.PERCENTAGE }
          })
        ]
      })
    );

    const doc = new Document({
      styles: {
        default: {
          document: {
            run: { font: "Arial", size: 24 },
            paragraph: { bidirectional: true }
          }
        }
      },
      sections: [{
        properties: { 
          page: { margin: { top: 720, right: 720, bottom: 720, left: 720 } },
          bidi: true // RTL for entire section
        },
        children: [
          new Paragraph({
            children: [new TextRun({ text: "المركز الوطني التركي لزراعة الشعر", bold: true, size: 36 })],
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
            bidirectional: true
          }),
          new Paragraph({
            children: [new TextRun({ text: `ملف المريض: ${patient.name}`, bold: true, size: 32 })],
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
            bidirectional: true
          }),
          new Paragraph({
            children: [new TextRun({ text: `تاريخ التصدير: ${new Date().toLocaleDateString('en-GB')}`, size: 20 })],
            alignment: AlignmentType.CENTER,
            spacing: { after: 600 },
            bidirectional: true
          }),
          new Table({ 
            rows: tableRows, 
            width: { size: 100, type: WidthType.PERCENTAGE },
            visuallyRightToLeft: true
          }),
          ...(sessions.length > 0 ? [
            new Paragraph({ children: [new TextRun({ text: "", size: 24 })], spacing: { before: 400 } }),
            new Paragraph({
              children: [new TextRun({ text: "الجلسات:", bold: true, size: 28 })],
              alignment: AlignmentType.RIGHT,
              spacing: { after: 200 },
              bidirectional: true
            }),
            ...sessions.map((s, i) => new Paragraph({
              children: [new TextRun({ text: `${i+1}. ${s.session_type} - ${s.date} ${s.notes ? `(${s.notes})` : ""}`, size: 22 })],
              alignment: AlignmentType.RIGHT,
              bidirectional: true
            }))
          ] : [])
        ]
      }]
    });

    return await Packer.toBlob(doc);
  };

  // تصدير ZIP جماعي لكل المرضى (نسخ احتياطي) - Word + صور
  const exportAllPatientsZip = async () => {
    if (filteredPatients.length === 0) {
      toast.error("لا يوجد مرضى للتصدير");
      return;
    }
    
    toast.info("جاري تحضير النسخة الاحتياطية...");
    
    try {
      const JSZip = (await loadJSZip()).default;
      const { saveAs } = await loadFileSaver();
      const zip = new JSZip();
      const patientsFolder = zip.folder("نسخة_احتياطية_المرضى");
      
      // Process each patient
      for (let i = 0; i < filteredPatients.length; i++) {
        const patient = filteredPatients[i];
        const folderName = `${patient.medical_id || i+1}_${patient.name.replace(/\s+/g, '_')}`;
        const patientFolder = patientsFolder.folder(folderName);
        
        // Get sessions
        let sessions = [];
        try {
          const sessionsRes = await api.get(`/sessions?patient_id=${patient.id}`, token);
          sessions = sessionsRes.data || [];
        } catch (e) {}
        
        // Create Word document
        try {
          const wordBlob = await createPatientWordDoc(patient, sessions);
          patientFolder.file(`${patient.name}_ملف.docx`, wordBlob);
        } catch (e) {
          console.error("Error creating Word doc:", e);
        }
        
        // Get photos
        try {
          const photosRes = await api.get(`/photos?patient_id=${patient.id}`, token);
          if (photosRes.data.length > 0) {
            const photosFolder = patientFolder.folder("الصور");
            for (let j = 0; j < photosRes.data.length; j++) {
              const photo = photosRes.data[j];
              const photoUrl = photo.image_url ? resolveUrl(photo.image_url) : null;
              if (photoUrl) {
                try {
                  const imgRes = await fetch(photoUrl);
                  const imgBlob = await imgRes.blob();
                  const photoType = photo.photo_type === "before" ? "قبل" : photo.photo_type === "after" ? "بعد" : "أثناء";
                  photosFolder.file(`${photoType}_${j+1}.jpg`, imgBlob);
                } catch (e) { console.error("Photo fetch error:", e); }
              } else if (photo.image_data) {
                const base64Data = photo.image_data.split(',')[1] || photo.image_data;
                const photoType = photo.photo_type === "before" ? "قبل" : photo.photo_type === "after" ? "بعد" : "أثناء";
                photosFolder.file(`${photoType}_${j+1}.jpg`, base64Data, {base64: true});
              }
            }
          }
        } catch (e) {}
        
        // Update progress
        if ((i + 1) % 3 === 0 || i === filteredPatients.length - 1) {
          toast.info(`تم معالجة ${i + 1} من ${filteredPatients.length} مريض`);
        }
      }
      
      // Generate and download
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `نسخة_احتياطية_${new Date().toISOString().split('T')[0]}.zip`);
      toast.success(`تم تصدير النسخة الاحتياطية بنجاح (${filteredPatients.length} مريض)`);
      
    } catch (error) {
      console.error(error);
      toast.error("خطأ في تصدير النسخة الاحتياطية");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPatient(null);
    setFormData(initialFormData);
    setActiveFormTab("personal");
    // Clear edit parameter from URL
    if (location.search.includes("edit=")) {
      navigate("/patients", { replace: true });
    }
  };

  const getFollowupBadge = (patient) => {
    if (!patient.next_followup_date) return null;
    const days = patient.days_until_followup;
    if (days === null || days === undefined) return null;
    
    if (days < 0) return <span className="badge-neutral px-2 py-1 rounded-full text-xs">فات الموعد</span>;
    if (days === 0) return <span className="badge-success px-2 py-1 rounded-full text-xs animate-pulse">اليوم</span>;
    if (days === 1) return <span className="badge-warning px-2 py-1 rounded-full text-xs">غداً</span>;
    if (days <= 7) return <span className="badge-info px-2 py-1 rounded-full text-xs">بعد {days} أيام</span>;
    return null;
  };

  const formTabs = [
    { id: "personal", label: "المعلومات الشخصية" },
    { id: "medical", label: "المعلومات الطبية" },
    { id: "procedure", label: "معلومات العملية" },
    { id: "package", label: "الباقة المشمولة" },
    { id: "booking", label: "الحجز والمدفوعات" }
  ];

  return (
    <div className="animate-fade-in" data-testid="patients-page">
      <div className="page-header flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="page-title">المرضى ({filteredPatients.length})</h1>
          <p className="page-subtitle">إدارة ملفات المرضى</p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <button onClick={exportAllPatientsZip} className="btn btn-secondary !p-2 sm:!px-3" data-testid="backup-btn" title="نسخ احتياطي ZIP">
            <Database className="w-4 h-4" />
            <span className="hidden md:inline text-xs">نسخ احتياطي</span>
          </button>
          <button onClick={exportPatientsToExcel} className="btn btn-secondary !p-2 sm:!px-3" data-testid="export-excel-btn" title="تصدير Excel">
            <FileSpreadsheet className="w-4 h-4" />
            <span className="hidden md:inline text-xs">Excel</span>
          </button>
          <button onClick={() => {
            const link = document.createElement('a');
            link.href = `${API}/patients/export/vcf`;
            link.setAttribute('download', 'vatan_hair_patients.vcf');
            const t = localStorage.getItem('token');
            fetch(`${API}/patients/export/vcf`, { headers: { Authorization: `Bearer ${t}` } })
              .then(r => r.blob())
              .then(blob => { const url = URL.createObjectURL(blob); link.href = url; link.click(); URL.revokeObjectURL(url); toast.success('تم تنزيل جهات الاتصال'); })
              .catch(() => toast.error('خطأ في التصدير'));
          }} className="btn btn-secondary !p-2 sm:!px-3" data-testid="export-vcf-btn" title="تصدير جهات اتصال">
            <Phone className="w-4 h-4" />
            <span className="hidden md:inline text-xs">جهات اتصال</span>
          </button>
          <button onClick={openNewPatientModal} className="btn btn-primary !py-2 !px-3" data-testid="add-patient-btn">
            <Plus className="w-4 h-4" />
            <span className="text-sm">إضافة مريض</span>
          </button>
        </div>
      </div>

      <div className="data-table mb-6">
        <div className="p-4">
          <div className="flex gap-4 items-center flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="البحث عن مريض..."
                className="form-input pr-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="search-patients"
              />
            </div>
            <select
              className="form-input w-auto"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              data-testid="filter-type"
            >
              <option value="all">جميع المرضى</option>
              <option value="center">مرضى المركز</option>
              <option value="hall_rental">مرضى التأجير</option>
            </select>
            <select
              className="form-input w-auto"
              value={filterTransplantType}
              onChange={(e) => setFilterTransplantType(e.target.value)}
              data-testid="filter-transplant-type"
            >
              <option value="all">جميع الأنواع</option>
              <option value="hair">شعر</option>
              <option value="beard">لحية</option>
              <option value="eyebrow">حاجب</option>
            </select>
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`btn ${showAdvancedFilters ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2`}
              data-testid="advanced-filters-btn"
            >
              <Filter className="w-4 h-4" />
              فلاتر متقدمة
            </button>
            <button
              onClick={() => setFilterFollowup(!filterFollowup)}
              className={`btn ${filterFollowup ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2`}
              data-testid="filter-followup-btn"
            >
              <Bell className="w-4 h-4" />
              المراجعات القادمة
            </button>
          </div>
          
          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200 animate-fade-in">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">الحالة المالية</label>
                  <select
                    className="form-input"
                    value={filterFinancial}
                    onChange={(e) => setFilterFinancial(e.target.value)}
                    data-testid="filter-financial"
                  >
                    <option value="all">الكل</option>
                    <option value="paid">مسدد بالكامل ✅</option>
                    <option value="pending">متبقي عليه 🟡</option>
                    <option value="unpaid">لم يدفع 🔴</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">تاريخ العملية (من)</label>
                  <input
                    type="date"
                    className="form-input"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    data-testid="filter-date-from"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">تاريخ العملية (إلى)</label>
                  <input
                    type="date"
                    className="form-input"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    data-testid="filter-date-to"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">ترتيب حسب</label>
                  <select
                    className="form-input"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    data-testid="sort-by"
                  >
                    <option value="operation_date">تاريخ العملية (الأحدث)</option>
                    <option value="created_at">تاريخ الإضافة</option>
                    <option value="name">الاسم (أبجدي)</option>
                  </select>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => {
                    setFilterFinancial("all");
                    setFilterDateFrom("");
                    setFilterDateTo("");
                    setSortBy("operation_date");
                  }}
                  className="btn btn-secondary btn-sm"
                >
                  إعادة تعيين الفلاتر
                </button>
                <span className="text-sm text-gray-500 self-center">
                  {patients.length} نتيجة
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">جاري التحميل...</div>
      ) : filteredPatients.length === 0 ? (
        <div className="empty-state">
          <Users className="empty-state-icon" />
          <h3>لا يوجد مرضى</h3>
          <p>ابدأ بإضافة مريض جديد</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedPatients.map((patient) => (
            <div
              key={patient.id}
              className="patient-card"
              onClick={() => navigate(`/patients/${patient.id}`)}
              data-testid={`patient-card-${patient.id}`}
            >
              <div className="flex items-center gap-4">
                <div className="patient-avatar">
                  {patient.name.charAt(0)}
                </div>
                <div className="patient-info flex-1">
                  <h3 className="flex items-center gap-2">
                    {patient.name}
                    {patient.medical_id && (
                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">#{patient.medical_id}</span>
                    )}
                    {patient.patient_type === "hall_rental" && (
                      <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">تأجير</span>
                    )}
                    {patient.transplant_type && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        patient.transplant_type === "hair" ? "bg-purple-100 text-purple-700" :
                        patient.transplant_type === "beard" ? "bg-green-100 text-green-700" :
                        "bg-pink-100 text-pink-700"
                      }`}>
                        {patient.transplant_type === "hair" ? "شعر" : patient.transplant_type === "beard" ? "لحية" : "حاجب"}
                      </span>
                    )}
                  </h3>
                  <p className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {patient.phone}
                  </p>
                </div>
                <button
                  onClick={(e) => openEditModal(patient, e)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  data-testid={`edit-patient-${patient.id}`}
                >
                  <Edit className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <div className="flex gap-4 mt-4 pt-4 border-t border-gray-100 text-sm text-gray-500 items-center flex-wrap">
                <span>{patient.gender === "male" ? "ذكر" : "أنثى"}</span>
                {patient.dob && <span>{calculateAge(patient.dob)} سنة</span>}
                <span>{patient.total_sessions} جلسة</span>
                {patient.transplant_date && (
                  <span className="text-blue-600">📅 {patient.transplant_date}</span>
                )}
                {getFollowupBadge(patient)}
              </div>
              {patient.operation_date && (
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  <span className="text-gray-600">موعد العملية: {patient.operation_date}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6" data-testid="pagination">
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
            className="px-3 py-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 text-sm font-medium">السابق</button>
          <div className="flex gap-1">
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let page;
              if (totalPages <= 7) page = i + 1;
              else if (currentPage <= 4) page = i + 1;
              else if (currentPage >= totalPages - 3) page = totalPages - 6 + i;
              else page = currentPage - 3 + i;
              return (
                <button key={page} onClick={() => setCurrentPage(page)}
                  className={`w-9 h-9 rounded-lg text-sm font-bold ${currentPage === page ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{page}</button>
              );
            })}
          </div>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
            className="px-3 py-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 text-sm font-medium">التالي</button>
          <span className="text-xs text-gray-400 mr-2">صفحة {currentPage} من {totalPages} ({filteredPatients.length} مريض)</span>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingPatient ? "تعديل بيانات المريض" : "إضافة مريض جديد"}</h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Form Tabs */}
            <div className="px-6 pt-4">
              <div className="tabs">
                {formTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`tab ${activeFormTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveFormTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div ref={modalBodyRef} className="modal-body max-h-[60vh] overflow-y-auto">
                
                {/* المعلومات الشخصية */}
                {activeFormTab === "personal" && (
                  <div className="grid grid-cols-2 gap-4 animate-fade-in">
                    <div className="form-group">
                      <label className="form-label">الاسم *</label>
                      <input type="text" className="form-input" value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required data-testid="patient-name-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">اللقب</label>
                      <input type="text" className="form-input" value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">رقم الهاتف *</label>
                      <input type="tel" className="form-input" value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required data-testid="patient-phone-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">تاريخ الميلاد *</label>
                      <input type="date" className="form-input" value={formData.dob}
                        onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                        required data-testid="patient-dob-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">الجنس *</label>
                      <select className="form-input" value={formData.gender}
                        onChange={(e) => setFormData({ ...formData, gender: e.target.value })}>
                        <option value="male">ذكر</option>
                        <option value="female">أنثى</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">الوظيفة</label>
                      <input type="text" className="form-input" value={formData.occupation}
                        onChange={(e) => setFormData({ ...formData, occupation: e.target.value })} />
                    </div>
                    <div className="form-group col-span-2">
                      <label className="form-label">العنوان</label>
                      <input type="text" className="form-input" value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="المدينة - المنطقة" />
                    </div>
                  </div>
                )}

                {/* المعلومات الطبية */}
                {activeFormTab === "medical" && (
                  <div className="grid grid-cols-2 gap-4 animate-fade-in">
                    <div className="form-group">
                      <label className="form-label">فصيلة الدم</label>
                      <select className="form-input" value={formData.blood_type}
                        onChange={(e) => setFormData({ ...formData, blood_type: e.target.value })}>
                        <option value="">غير محدد</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">هل أجريت عملية من قبل؟</label>
                      <select className="form-input" value={formData.previous_surgery}
                        onChange={(e) => setFormData({ ...formData, previous_surgery: e.target.value, previous_surgery_details: e.target.value === "no" ? "" : formData.previous_surgery_details })}>
                        <option value="">اختر</option>
                        <option value="no">لا</option>
                        <option value="yes">نعم</option>
                      </select>
                    </div>
                    {formData.previous_surgery === "yes" && (
                      <div className="form-group col-span-2">
                        <label className="form-label">تفاصيل العملية السابقة</label>
                        <input type="text" className="form-input" value={formData.previous_surgery_details}
                          onChange={(e) => setFormData({ ...formData, previous_surgery_details: e.target.value })}
                          placeholder="اذكر تفاصيل العملية" />
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">هل لديك حساسية؟</label>
                      <select className="form-input" value={formData.has_allergies}
                        onChange={(e) => setFormData({ ...formData, has_allergies: e.target.value, allergies: e.target.value === "no" ? "" : formData.allergies })}>
                        <option value="">اختر</option>
                        <option value="no">لا</option>
                        <option value="yes">نعم</option>
                      </select>
                    </div>
                    {formData.has_allergies === "yes" && (
                      <div className="form-group">
                        <label className="form-label">تفاصيل الحساسية</label>
                        <input type="text" className="form-input" value={formData.allergies}
                          onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                          placeholder="اذكر نوع الحساسية" />
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">هل تتناول أدوية؟</label>
                      <select className="form-input" value={formData.has_medications}
                        onChange={(e) => setFormData({ ...formData, has_medications: e.target.value, medications: e.target.value === "no" ? "" : formData.medications })}>
                        <option value="">اختر</option>
                        <option value="no">لا</option>
                        <option value="yes">نعم</option>
                      </select>
                    </div>
                    {formData.has_medications === "yes" && (
                      <div className="form-group">
                        <label className="form-label">الأدوية المستخدمة</label>
                        <input type="text" className="form-input" value={formData.medications}
                          onChange={(e) => setFormData({ ...formData, medications: e.target.value })}
                          placeholder="اذكر الأدوية" />
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">التدخين</label>
                      <select className="form-input" value={formData.smoking}
                        onChange={(e) => setFormData({ ...formData, smoking: e.target.value })}>
                        <option value="">اختر</option>
                        <option value="no">لا</option>
                        <option value="yes">نعم</option>
                        <option value="former">سابقاً</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">الكحول</label>
                      <select className="form-input" value={formData.alcohol}
                        onChange={(e) => setFormData({ ...formData, alcohol: e.target.value })}>
                        <option value="">اختر</option>
                        <option value="no">لا</option>
                        <option value="yes">نعم</option>
                        <option value="former">سابقاً</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">ضغط الدم</label>
                      <select className="form-input" value={formData.blood_pressure}
                        onChange={(e) => setFormData({ ...formData, blood_pressure: e.target.value })}>
                        <option value="">اختر</option>
                        <option value="normal">طبيعي</option>
                        <option value="high">مرتفع</option>
                        <option value="low">منخفض</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">السكري</label>
                      <select className="form-input" value={formData.diabetes}
                        onChange={(e) => setFormData({ ...formData, diabetes: e.target.value })}>
                        <option value="">اختر</option>
                        <option value="no">لا</option>
                        <option value="yes">نعم</option>
                        <option value="prediabetes">مقدمات السكري</option>
                      </select>
                    </div>
                    <div className="form-group col-span-2">
                      <label className="form-label">سبب تساقط الشعر</label>
                      <select className="form-input" value={formData.hair_loss_cause}
                        onChange={(e) => setFormData({ ...formData, hair_loss_cause: e.target.value })}>
                        <option value="">اختر</option>
                        <option value="genetic">عامل وراثي</option>
                        <option value="vitamin_deficiency">نقص فيتامينات</option>
                        <option value="psychological">حالة نفسية</option>
                        <option value="other">غير ذلك</option>
                      </select>
                    </div>
                    <div className="form-group col-span-2">
                      <label className="form-label">التاريخ الطبي</label>
                      <textarea className="form-input" rows={2} value={formData.medical_history}
                        onChange={(e) => setFormData({ ...formData, medical_history: e.target.value })}
                        placeholder="أي معلومات طبية إضافية" />
                    </div>
                  </div>
                )}

                {/* معلومات العملية */}
                {activeFormTab === "procedure" && (
                  <div className="grid grid-cols-2 gap-4 animate-fade-in">
                    <div className="form-group">
                      <label className="form-label">نوع المريض</label>
                      <select className="form-input" value={formData.patient_type}
                        onChange={(e) => setFormData({ ...formData, patient_type: e.target.value })}>
                        <option value="">اختر</option>
                        <option value="center">مريض المركز الوطني التركي</option>
                        <option value="hall_rental">تأجير صالة</option>
                      </select>
                    </div>
                    {formData.patient_type === "hall_rental" && (
                      <div className="form-group">
                        <label className="form-label">تأجير صالة من</label>
                        <input type="text" className="form-input" value={formData.hall_rental_from}
                          onChange={(e) => setFormData({ ...formData, hall_rental_from: e.target.value })} />
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">نوع الزراعة</label>
                      <select className="form-input" value={formData.transplant_type}
                        onChange={(e) => setFormData({ ...formData, transplant_type: e.target.value })}>
                        <option value="">اختر</option>
                        <option value="hair">شعر</option>
                        <option value="beard">لحية</option>
                        <option value="eyebrow">حاجب</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">جلسة أولى أم ثانية</label>
                      <select className="form-input" value={formData.session_type_first_or_second}
                        onChange={(e) => setFormData({ ...formData, session_type_first_or_second: e.target.value })}>
                        <option value="">اختر</option>
                        <option value="first">جلسة أولى (زراعة أولى)</option>
                        <option value="second">جلسة ثانية (تكثيف)</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">الاقتطاف</label>
                      <select className="form-input" value={formData.extraction_by}
                        onChange={(e) => setFormData({ ...formData, extraction_by: e.target.value, extraction_by_other: e.target.value === "other" ? formData.extraction_by_other : "" })}>
                        <option value="">اختر</option>
                        <option value="علي عدنان">علي عدنان</option>
                        <option value="قاسم الافندي">قاسم الافندي</option>
                        <option value="محمود الافندي">محمود الافندي</option>
                        <option value="other">أخرى</option>
                      </select>
                    </div>
                    {formData.extraction_by === "other" && (
                      <div className="form-group">
                        <label className="form-label">اسم المقتطف</label>
                        <input type="text" className="form-input" value={formData.extraction_by_other}
                          onChange={(e) => setFormData({ ...formData, extraction_by_other: e.target.value })}
                          placeholder="اكتب الاسم" />
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">فتح القنوات</label>
                      <select className="form-input" value={formData.channel_opening_by}
                        onChange={(e) => setFormData({ ...formData, channel_opening_by: e.target.value, channel_opening_by_other: e.target.value === "other" ? formData.channel_opening_by_other : "" })}>
                        <option value="">اختر</option>
                        <option value="علي عدنان">علي عدنان</option>
                        <option value="محمود الافندي">محمود الافندي</option>
                        <option value="other">أخرى</option>
                      </select>
                    </div>
                    {formData.channel_opening_by === "other" && (
                      <div className="form-group">
                        <label className="form-label">اسم فاتح القنوات</label>
                        <input type="text" className="form-input" value={formData.channel_opening_by_other}
                          onChange={(e) => setFormData({ ...formData, channel_opening_by_other: e.target.value })}
                          placeholder="اكتب الاسم" />
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">عدد البصيلات المزروعة</label>
                      <input type="number" className="form-input" value={formData.grafts_count}
                        onChange={(e) => setFormData({ ...formData, grafts_count: e.target.value })}
                        placeholder="رقم فقط" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">نوع الشعر</label>
                      <select className="form-input" value={formData.hair_type}
                        onChange={(e) => setFormData({ ...formData, hair_type: e.target.value })}>
                        <option value="">اختر</option>
                        <option value="fine">خفيف</option>
                        <option value="normal">عادي</option>
                        <option value="coarse">خشن</option>
                        <option value="curly">مجعد</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">زراعة 1</label>
                      <select className="form-input" value={formData.transplant_1}
                        onChange={(e) => setFormData({ ...formData, transplant_1: e.target.value })}>
                        <option value="">اختر</option>
                        {staffMembers.planting.map((name, i) => (
                          <option key={i} value={name}>{name}</option>
                        ))}
                        <option value="other">أخرى</option>
                      </select>
                    </div>
                    {formData.transplant_1 === "other" && (
                      <div className="form-group">
                        <label className="form-label">اسم الزارع 1</label>
                        <input type="text" className="form-input" 
                          onChange={(e) => setFormData({ ...formData, transplant_1: e.target.value })}
                          placeholder="اكتب الاسم" />
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">زراعة 2</label>
                      <select className="form-input" value={formData.transplant_2}
                        onChange={(e) => setFormData({ ...formData, transplant_2: e.target.value })}>
                        <option value="">اختر</option>
                        {staffMembers.planting.map((name, i) => (
                          <option key={i} value={name}>{name}</option>
                        ))}
                        <option value="other">أخرى</option>
                      </select>
                    </div>
                    {formData.transplant_2 === "other" && (
                      <div className="form-group">
                        <label className="form-label">اسم الزارع 2</label>
                        <input type="text" className="form-input" 
                          onChange={(e) => setFormData({ ...formData, transplant_2: e.target.value })}
                          placeholder="اكتب الاسم" />
                      </div>
                    )}
                  </div>
                )}

                {/* الباقة المشمولة */}
                {activeFormTab === "package" && (
                  <div className="grid grid-cols-2 gap-4 animate-fade-in">
                    <div className="form-group">
                      <label className="form-label">عدد جلسات البلازما المشمولة</label>
                      <input type="number" className="form-input" value={formData.package_plasma_sessions}
                        onChange={(e) => setFormData({ ...formData, package_plasma_sessions: e.target.value })}
                        placeholder="مثال: 6" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">عدد جلسات الميزو المشمولة</label>
                      <input type="number" className="form-input" value={formData.package_meso_sessions}
                        onChange={(e) => setFormData({ ...formData, package_meso_sessions: e.target.value })}
                        placeholder="مثال: 3" />
                    </div>
                    <div className="col-span-2 border-t pt-4 mt-2">
                      <h4 className="font-medium text-gray-700 mb-3">حالة الجلسات (للمرضى القدام)</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="form-group">
                          <label className="form-label">وصل للجلسة رقم</label>
                          <input type="number" className="form-input" value={formData.completed_sessions_before}
                            onChange={(e) => setFormData({ ...formData, completed_sessions_before: e.target.value })}
                            placeholder="مثال: 5"
                            data-testid="completed-sessions-before" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">حالة الجلسات</label>
                          <select className="form-input" value={formData.sessions_status}
                            onChange={(e) => setFormData({ ...formData, sessions_status: e.target.value })}
                            data-testid="sessions-status-select">
                            <option value="">-- اختر --</option>
                            <option value="ongoing">مستمر بالجلسات</option>
                            <option value="completed">أكمل جميع الجلسات</option>
                            <option value="stopped">متوقف</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2 border-t pt-4 mt-2">
                      <h4 className="font-medium text-gray-700 mb-3">المستلزمات المسلمة</h4>
                      <div className="flex flex-wrap gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" className="w-5 h-5 rounded border-gray-300 text-blue-600"
                            checked={formData.shampoo_delivered}
                            onChange={(e) => setFormData({ ...formData, shampoo_delivered: e.target.checked })} />
                          <span>شامبو</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" className="w-5 h-5 rounded border-gray-300 text-blue-600"
                            checked={formData.lotion_delivered}
                            onChange={(e) => setFormData({ ...formData, lotion_delivered: e.target.checked })} />
                          <span>لوشن</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" className="w-5 h-5 rounded border-gray-300 text-blue-600"
                            checked={formData.vitamins_delivered}
                            onChange={(e) => setFormData({ ...formData, vitamins_delivered: e.target.checked })} />
                          <span>فيتامين</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* الحجز والمدفوعات */}
                {activeFormTab === "booking" && (
                  <div className="grid grid-cols-2 gap-4 animate-fade-in">
                    <div className="form-group">
                      <label className="form-label">الزبون عن طريق (مريض آخر)</label>
                      <select className="form-input" value={formData.referred_by_patient_id}
                        onChange={(e) => setFormData({ ...formData, referred_by_patient_id: e.target.value })}>
                        <option value="">-- اختر مريض --</option>
                        {allPatients.filter(p => p.id !== editingPatient?.id).map(p => (
                          <option key={p.id} value={p.id}>{p.name} - {p.phone}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">مصدر الإحالة</label>
                      <input type="text" className="form-input" value={formData.referral_source}
                        onChange={(e) => setFormData({ ...formData, referral_source: e.target.value })}
                        placeholder="انستغرام، فيسبوك، صديق..." />
                    </div>
                    <div className="form-group">
                      <label className="form-label">الحجز عن طريق</label>
                      <input type="text" className="form-input" value={formData.booking_source}
                        onChange={(e) => setFormData({ ...formData, booking_source: e.target.value })}
                        placeholder="طريقة الحجز" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">مدخل البيانات</label>
                      <select className="form-input" value={formData.data_entry_by}
                        onChange={(e) => setFormData({ ...formData, data_entry_by: e.target.value })}>
                        <option value="">اختر</option>
                        {staffMembers.dataEntry.map((name, i) => (
                          <option key={i} value={name}>{name}</option>
                        ))}
                        <option value="other">أخرى</option>
                      </select>
                    </div>
                    {formData.data_entry_by === "other" && (
                      <div className="form-group">
                        <label className="form-label">اسم مدخل البيانات</label>
                        <input type="text" className="form-input"
                          onChange={(e) => setFormData({ ...formData, data_entry_by: e.target.value })}
                          placeholder="اكتب الاسم" />
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">العملة</label>
                      <select className="form-input" value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}>
                        <option value="IQD">دينار عراقي (IQD)</option>
                        <option value="USD">دولار أمريكي ($)</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">المبلغ المدفوع</label>
                      <input type="number" className="form-input" value={formData.total_paid}
                        onChange={(e) => {
                          const paid = parseFloat(e.target.value) || 0;
                          const remaining = parseFloat(formData.remaining_amount) || 0;
                          const newPrice = paid + remaining;
                          const pct = parseFloat(formData.installment_percent) || 15;
                          const updates = { total_paid: e.target.value, price: newPrice.toString() };
                          if (formData.payment_type === "installment" && newPrice > 0) {
                            updates.installment_amount = Math.round(newPrice * pct / 100).toString();
                          }
                          setFormData({ ...formData, ...updates });
                        }}
                        placeholder="0" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">المبلغ المتبقي (إن وجد دين)</label>
                      <input type="number" className="form-input" value={formData.remaining_amount}
                        onChange={(e) => {
                          const remaining = Math.max(0, parseFloat(e.target.value) || 0);
                          const paid = parseFloat(formData.total_paid) || 0;
                          const newPrice = paid + remaining;
                          const pct = parseFloat(formData.installment_percent) || 15;
                          const updates = { 
                            remaining_amount: remaining > 0 ? remaining.toString() : "",
                            price: newPrice.toString()
                          };
                          if (formData.payment_type === "installment" && newPrice > 0) {
                            updates.installment_amount = Math.round(newPrice * pct / 100).toString();
                          }
                          setFormData({ ...formData, ...updates });
                        }}
                        min="0"
                        placeholder="0 إذا مسدد بالكامل" />
                    </div>
                    
                    {/* نسب الكادر */}
                    <div className="col-span-2 border-t pt-4 mt-2">
                      <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-green-600" />
                        نسب الكادر (تُخصم من السعر)
                      </h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="form-group">
                          <label className="form-label">نسبة الاقتطاف</label>
                          <input type="number" className="form-input" value={formData.extraction_cost}
                            onChange={(e) => setFormData({ ...formData, extraction_cost: e.target.value })}
                            placeholder="مثال: 100000" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">نسبة القنوات</label>
                          <input type="number" className="form-input" value={formData.channels_cost}
                            onChange={(e) => setFormData({ ...formData, channels_cost: e.target.value })}
                            placeholder="مثال: 100000" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">نسبة الزراعة</label>
                          <input type="number" className="form-input" value={formData.planting_cost}
                            onChange={(e) => setFormData({ ...formData, planting_cost: e.target.value })}
                            placeholder="مثال: 60000" />
                        </div>
                      </div>
                      <div className="mt-3 p-3 bg-green-50 rounded-lg">
                        <span className="text-green-700 font-medium">
                          الصافي: {((parseFloat(formData.price) || 0) - (parseFloat(formData.extraction_cost) || 0) - (parseFloat(formData.channels_cost) || 0) - (parseFloat(formData.planting_cost) || 0)).toLocaleString()} {formData.currency}
                        </span>
                      </div>
                    </div>
                    
                    {/* الأقساط */}
                    <div className="col-span-2 border-t pt-4 mt-2">
                      <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-blue-600" />
                        نوع الدفع والأقساط
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="form-group">
                          <label className="form-label">نوع الدفع</label>
                          <select className="form-input" value={formData.payment_type || "cash"}
                            onChange={(e) => {
                              const newType = e.target.value;
                              const price = parseFloat(formData.price) || 0;
                              const pct = parseFloat(formData.installment_percent) || 15;
                              const updates = { payment_type: newType };
                              if (newType === "installment" && price > 0) {
                                updates.installment_amount = Math.round(price * pct / 100).toString();
                              }
                              setFormData({ ...formData, ...updates });
                            }}>
                            <option value="cash">نقدي / كامل</option>
                            <option value="installment">أقساط (شركة تمويل)</option>
                          </select>
                        </div>
                        {formData.payment_type === "installment" && (() => {
                          const price = parseFloat(formData.price) || 0;
                          const pct = parseFloat(formData.installment_percent) || 15;
                          const autoAmount = price > 0 ? Math.round(price * pct / 100) : 0;
                          const currentAmount = parseFloat(formData.installment_amount) || autoAmount;
                          return (
                          <>
                            <div className="form-group">
                              <label className="form-label">شركة التمويل</label>
                              <select className="form-input" value={formData.installment_company || ""}
                                onChange={(e) => setFormData({ ...formData, installment_company: e.target.value })}>
                                <option value="">-- اختر الشركة --</option>
                                <option value="الرافدين">الرافدين</option>
                                <option value="other">أخرى</option>
                              </select>
                            </div>
                            {formData.installment_company === "other" && (
                              <div className="form-group">
                                <label className="form-label">اسم الشركة</label>
                                <input type="text" className="form-input" value={formData.installment_company_other || ""}
                                  onChange={(e) => setFormData({ ...formData, installment_company_other: e.target.value })}
                                  placeholder="اكتب اسم الشركة" />
                              </div>
                            )}
                            <div className="form-group">
                              <label className="form-label">نسبة خصم الشركة (%)</label>
                              <div className="flex items-center gap-2">
                                <input type="number" className="form-input" value={formData.installment_percent || "15"}
                                  onChange={(e) => {
                                    const newPct = parseFloat(e.target.value) || 0;
                                    const amt = price > 0 ? Math.round(price * newPct / 100) : 0;
                                    setFormData({ ...formData, installment_percent: e.target.value, installment_amount: amt.toString() });
                                  }}
                                  placeholder="15" min="0" max="100" />
                                <span className="text-xs text-gray-500 whitespace-nowrap">%</span>
                              </div>
                            </div>
                            <div className="form-group">
                              <label className="form-label">مبلغ الخصم {price > 0 ? `(${pct}% = ${autoAmount.toLocaleString()})` : ""}</label>
                              <input type="number" className="form-input font-bold" value={formData.installment_amount || autoAmount || ""}
                                onChange={(e) => {
                                  const newAmt = parseFloat(e.target.value) || 0;
                                  const newPct = price > 0 ? Math.round(newAmt / price * 100) : 0;
                                  setFormData({ ...formData, installment_amount: e.target.value, installment_percent: newPct.toString() });
                                }}
                                placeholder="يحسب تلقائي" />
                            </div>
                            <div className="form-group">
                              <label className="form-label">تاريخ وصول المبلغ المتوقع</label>
                              <input type="date" className="form-input" value={formData.installment_expected_date || ""}
                                onChange={(e) => setFormData({ ...formData, installment_expected_date: e.target.value })} />
                            </div>
                            <div className="form-group">
                              <label className="form-label">حالة القسط</label>
                              <select className="form-input" value={formData.installment_status || "pending"}
                                onChange={(e) => {
                                  const newStatus = e.target.value;
                                  const updates = { installment_status: newStatus };
                                  if (newStatus === "received") {
                                    updates.installment_received_date = new Date().toISOString().split('T')[0];
                                  }
                                  setFormData({ ...formData, ...updates });
                                }}>
                                <option value="pending">بانتظار التحويل</option>
                                <option value="received">تم الاستلام</option>
                              </select>
                            </div>
                            {formData.installment_status === "received" && (
                              <div className="form-group">
                                <label className="form-label">تاريخ الاستلام الفعلي</label>
                                <input type="date" className="form-input" value={formData.installment_received_date || new Date().toISOString().split('T')[0]}
                                  onChange={(e) => setFormData({ ...formData, installment_received_date: e.target.value })} />
                              </div>
                            )}
                            {price > 0 && (
                              <div className="col-span-2 p-3 bg-amber-50 rounded-lg text-sm">
                                <div className="flex justify-between mb-1">
                                  <span className="text-gray-600">سعر العملية:</span>
                                  <span className="font-bold">{price.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between mb-1">
                                  <span className="text-gray-600">خصم الشركة ({pct}%):</span>
                                  <span className="font-bold text-red-600">-{currentAmount.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between border-t pt-1 mt-1">
                                  <span className="text-gray-700 font-medium">المبلغ اللي يوصلك:</span>
                                  <span className="font-bold text-emerald-600 text-lg">{(price - currentAmount).toLocaleString()}</span>
                                </div>
                              </div>
                            )}
                          </>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">موعد العملية</label>
                      <input type="date" className="form-input" value={formData.operation_date}
                        onChange={(e) => setFormData({ ...formData, operation_date: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">موعد المراجعة القادمة</label>
                      <input type="date" className="form-input" value={formData.next_followup_date}
                        onChange={(e) => setFormData({ ...formData, next_followup_date: e.target.value })} />
                    </div>
                    <div className="form-group col-span-2">
                      <label className="form-label">ملاحظات</label>
                      <textarea className="form-input" rows={3} value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="أي ملاحظات إضافية" />
                    </div>
                  </div>
                )}
                
              </div>
              
              {/* Navigation Buttons */}
              <div className="px-6 py-3 bg-gray-50 border-t flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => {
                    const currentIndex = formTabs.findIndex(t => t.id === activeFormTab);
                    if (currentIndex > 0) setActiveFormTab(formTabs[currentIndex - 1].id);
                  }}
                  className={`btn btn-secondary flex items-center gap-2 ${
                    formTabs.findIndex(t => t.id === activeFormTab) === 0 ? 'invisible' : ''
                  }`}
                >
                  <ChevronLeft className="w-4 h-4 rotate-180" />
                  السابق
                </button>
                
                <div className="flex gap-1">
                  {formTabs.map((tab, index) => (
                    <div
                      key={tab.id}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        activeFormTab === tab.id ? 'bg-blue-500' : 
                        formTabs.findIndex(t => t.id === activeFormTab) > index ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
                
                {formTabs.findIndex(t => t.id === activeFormTab) < formTabs.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => {
                      const currentIndex = formTabs.findIndex(t => t.id === activeFormTab);
                      if (currentIndex < formTabs.length - 1) setActiveFormTab(formTabs[currentIndex + 1].id);
                    }}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    التالي
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                ) : (
                  <button type="submit" className="btn btn-primary bg-green-600 hover:bg-green-700" data-testid="save-patient-btn">
                    <CheckCircle className="w-4 h-4" />
                    {editingPatient ? "حفظ التعديلات" : "حفظ المريض"}
                  </button>
                )}
              </div>
              
              <div className="modal-footer border-t-0 pt-0">
                <button type="button" onClick={closeModal} className="btn btn-secondary">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Patient Detail Page

// Complaints Section Component
const ComplaintsSection = ({ patientId, token }) => {
  const [complaints, setComplaints] = useState([]);
  const [newComplaint, setNewComplaint] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [loading, setLoading] = useState(true);

  const fetchComplaints = useCallback(async () => {
    try {
      const res = await api.get(`/patients/${patientId}/complaints`, token);
      setComplaints(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [patientId, token]);

  useEffect(() => { fetchComplaints(); }, [fetchComplaints]);

  const addComplaint = async () => {
    if (!newComplaint.trim()) { toast.error("اكتب نص الشكوى"); return; }
    try {
      await api.post(`/patients/${patientId}/complaints`, { patient_id: patientId, complaint_text: newComplaint, severity }, token);
      toast.success("تم تسجيل الشكوى");
      setNewComplaint(""); setSeverity("medium");
      fetchComplaints();
    } catch (err) { console.error(err); toast.error("خطأ"); }
  };

  const resolveComplaint = async (id) => {
    try {
      await api.put(`/complaints/${id}/resolve`, {}, token);
      toast.success("تم حل الشكوى");
      fetchComplaints();
    } catch (err) { console.error(err); toast.error("خطأ"); }
  };

  const fmtDate = (iso) => iso ? formatDateTime(iso) : "";
  const sevLabels = { low: "منخفضة", medium: "متوسطة", high: "عالية" };
  const sevColors = { low: "bg-blue-100 text-blue-700", medium: "bg-amber-100 text-amber-700", high: "bg-red-100 text-red-700" };

  return (
    <div className="data-table animate-slide-in p-6" data-testid="complaints-section">
      <h3 className="font-bold text-gray-800 mb-4">شكاوي المراجعين ({complaints.length})</h3>

      {/* Add Complaint */}
      <div className="bg-gray-50 rounded-xl p-4 mb-4">
        <textarea value={newComplaint} onChange={(e) => setNewComplaint(e.target.value)} placeholder="اكتب الشكوى هنا..."
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-red-400 focus:outline-none text-sm resize-none" rows={3} data-testid="complaint-input" />
        <div className="flex items-center justify-between mt-2">
          <div className="flex gap-2">
            {["low", "medium", "high"].map(s => (
              <button key={s} onClick={() => setSeverity(s)}
                className={`px-3 py-1 rounded-lg text-xs font-bold ${severity === s ? sevColors[s] : "bg-gray-100 text-gray-500"}`}>
                {sevLabels[s]}
              </button>
            ))}
          </div>
          <button onClick={addComplaint} className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700" data-testid="submit-complaint">
            تسجيل الشكوى
          </button>
        </div>
      </div>

      {/* Complaints List */}
      {loading ? <p className="text-center text-gray-400 py-4">جاري التحميل...</p> : complaints.length === 0 ? (
        <p className="text-center text-gray-400 py-8">لا توجد شكاوي مسجلة</p>
      ) : (
        <div className="space-y-2">
          {complaints.map(c => (
            <div key={c.id} className={`p-4 rounded-xl border ${c.status === "resolved" ? "bg-emerald-50 border-emerald-200" : "bg-white border-gray-200"}`} data-testid={`complaint-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm text-gray-800 mb-1">{c.complaint_text}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <span className={`px-2 py-0.5 rounded-full font-bold ${sevColors[c.severity] || sevColors.medium}`}>{sevLabels[c.severity] || "متوسطة"}</span>
                    <span>{fmtDate(c.created_at)}</span>
                    <span>بواسطة {c.created_by}</span>
                    {c.status === "resolved" && <span className="text-emerald-600 font-bold">✅ تم الحل - {c.resolved_by}</span>}
                  </div>
                </div>
                {c.status !== "resolved" && (
                  <button onClick={() => resolveComplaint(c.id)} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold hover:bg-emerald-200">تم الحل</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


const PatientDetailPage = () => {
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [patient, setPatient] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [activeTab, setActiveTab] = useState("info");
  const [loading, setLoading] = useState(true);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  const [photoData, setPhotoData] = useState({ photo_type: "before", description: "", image_data: "" });
  const [sessionData, setSessionData] = useState({ 
    session_type: "followup", 
    date: new Date().toISOString().split('T')[0], 
    notes: "", 
    cost_type: "free", 
    cost: "", 
    custom_type: "",
    next_review_date: ""
  });
  
  // WhatsApp State
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [whatsappSending, setWhatsappSending] = useState(false);
  const [whatsappReminderType, setWhatsappReminderType] = useState("custom");

  const fetchData = async () => {
    try {
      const [patientRes, sessionsRes, photosRes] = await Promise.all([
        api.get(`/patients/${id}`, token),
        api.get(`/sessions?patient_id=${id}`, token),
        api.get(`/photos?patient_id=${id}`, token)
      ]);
      setPatient(patientRes.data);
      setSessions(sessionsRes.data);
      setPhotos(photosRes.data);
    } catch (error) {
      toast.error("خطأ في تحميل بيانات المريض");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  // WhatsApp Send Function
  const sendWhatsApp = async () => {
    if (!patient?.phone) {
      toast.error("رقم هاتف المريض غير موجود");
      return;
    }
    
    setWhatsappSending(true);
    try {
      if (whatsappReminderType === "custom") {
        // Send custom message
        await api.post("/whatsapp/send", {
          phone: patient.phone,
          message: whatsappMessage,
          patient_id: id
        }, token);
      } else {
        // Send reminder
        await api.post(`/whatsapp/remind/${id}`, {
          patient_id: id,
          reminder_type: whatsappReminderType,
          custom_message: whatsappMessage || null
        }, token);
      }
      toast.success("تم إرسال الرسالة بنجاح! (حالة: في قائمة الانتظار) تأكد أن المستلم مسجل في Sandbox");
      setShowWhatsAppModal(false);
      setWhatsappMessage("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "فشل إرسال الرسالة");
    } finally {
      setWhatsappSending(false);
    }
  };

  const [multiplePhotos, setMultiplePhotos] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const readFiles = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve({
            name: file.name,
            data: reader.result,
            size: file.size
          });
        };
        reader.readAsDataURL(file);
      });
    });
    
    Promise.all(readFiles).then(results => {
      setMultiplePhotos(results);
      if (results.length === 1) {
        setPhotoData({ ...photoData, image_data: results[0].data });
      }
    });
  };

  const removePhoto = (index) => {
    const updated = multiplePhotos.filter((_, i) => i !== index);
    setMultiplePhotos(updated);
    if (updated.length === 0) {
      setPhotoData({ ...photoData, image_data: "" });
    }
  };

  const submitPhoto = async () => {
    if (multiplePhotos.length === 0 && !photoData.image_data) {
      toast.error("يرجى اختيار صورة واحدة على الأقل");
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const photosToUpload = multiplePhotos.length > 0 
        ? multiplePhotos.map(p => p.data) 
        : [photoData.image_data];
      
      for (let i = 0; i < photosToUpload.length; i++) {
        await api.post("/photos", { 
          patient_id: id, 
          photo_type: photoData.photo_type,
          description: photoData.description,
          image_data: photosToUpload[i]
        }, token);
        setUploadProgress(Math.round(((i + 1) / photosToUpload.length) * 100));
      }
      
      toast.success(`تم رفع ${photosToUpload.length} صورة بنجاح`);
      setShowPhotoModal(false);
      setPhotoData({ photo_type: "before", description: "", image_data: "" });
      setMultiplePhotos([]);
      setUploadProgress(0);
      fetchData();
    } catch (error) {
      toast.error("خطأ في رفع الصور");
    } finally {
      setIsUploading(false);
    }
  };

  const submitSession = async () => {
    if (!sessionData.date) {
      toast.error("يرجى تحديد التاريخ");
      return;
    }
    if (sessionData.session_type === "other" && !sessionData.custom_type) {
      toast.error("يرجى كتابة نوع الجلسة");
      return;
    }
    if (sessionData.cost_type === "paid" && !sessionData.cost) {
      toast.error("يرجى تحديد تكلفة الجلسة");
      return;
    }
    try {
      const finalSessionType = sessionData.session_type === "other" 
        ? sessionData.custom_type 
        : sessionData.session_type;
      
      await api.post("/sessions", {
        patient_id: id,
        session_type: finalSessionType,
        date: sessionData.date,
        notes: sessionData.notes,
        cost_type: sessionData.cost_type,
        cost: sessionData.cost_type === "paid" && sessionData.cost ? parseFloat(sessionData.cost) : 0,
        next_review_date: sessionData.next_review_date || null
      }, token);
      toast.success("تم إضافة الجلسة بنجاح");
      setShowSessionModal(false);
      setSessionData({ 
        session_type: "followup", 
        date: new Date().toISOString().split('T')[0], 
        notes: "", 
        cost_type: "free", 
        cost: "", 
        custom_type: "",
        next_review_date: ""
      });
      fetchData();
    } catch (error) {
      toast.error("خطأ في إضافة الجلسة");
    }
  };

  const deletePatient = async () => {
    if (window.confirm("هل أنت متأكد من حذف هذا المريض؟")) {
      try {
        await api.delete(`/patients/${id}`, token);
        toast.success("تم حذف المريض بنجاح");
        navigate("/patients");
      } catch (error) {
        toast.error("خطأ في حذف المريض");
      }
    }
  };

  const deletePhoto = async (photoId) => {
    if (window.confirm("هل أنت متأكد من حذف هذه الصورة؟")) {
      try {
        await api.delete(`/photos/${photoId}`, token);
        toast.success("تم حذف الصورة");
        fetchData();
      } catch (error) {
        toast.error("خطأ في حذف الصورة");
      }
    }
  };

  // Print Payment Receipt
  const printPaymentReceipt = () => {
    if (!patient) return;
    
    const currency = patient.currency === "USD" ? "$" : "د.ع";
    const today = new Date().toLocaleDateString('en-GB');
    const receiptNo = Date.now().toString().slice(-8);
    
    // Create printable HTML
    const printContent = `
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8">
        <title>إيصال دفع</title>
        <style>
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
          body {
            font-family: 'Segoe UI', Tahoma, sans-serif;
            max-width: 400px;
            margin: 0 auto;
            padding: 20px;
            direction: rtl;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #10B981;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .header h1 {
            color: #1F2937;
            font-size: 18px;
            margin: 0;
          }
          .header p {
            color: #6B7280;
            font-size: 12px;
            margin: 5px 0;
          }
          .receipt-title {
            background: linear-gradient(135deg, #10B981, #3B82F6);
            color: white;
            text-align: center;
            padding: 10px;
            border-radius: 8px;
            margin-bottom: 20px;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px dashed #E5E7EB;
          }
          .info-label {
            color: #6B7280;
            font-size: 13px;
          }
          .info-value {
            font-weight: bold;
            color: #1F2937;
          }
          .amount-box {
            background: #F3F4F6;
            border-radius: 10px;
            padding: 15px;
            margin: 20px 0;
          }
          .amount-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
          }
          .amount-row.total {
            border-top: 2px solid #10B981;
            margin-top: 10px;
            padding-top: 15px;
          }
          .amount-row.total .info-value {
            color: #10B981;
            font-size: 18px;
          }
          .remaining {
            color: #EF4444 !important;
          }
          .footer {
            text-align: center;
            margin-top: 30px;
            color: #9CA3AF;
            font-size: 11px;
          }
          .signatures {
            display: flex;
            justify-content: space-between;
            margin-top: 40px;
          }
          .sig-box {
            text-align: center;
            width: 45%;
          }
          .sig-line {
            border-top: 1px solid #6B7280;
            margin-bottom: 5px;
          }
          @media print {
            body { padding: 10px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>المركز الوطني التركي</h1>
          <p>لزراعة الشعر والتجميل</p>
        </div>
        
        <div class="receipt-title">
          <h2 style="margin:0; font-size:16px;">إيصال دفع</h2>
        </div>
        
        <div class="info-row">
          <span class="info-label">رقم الإيصال:</span>
          <span class="info-value">#${receiptNo}</span>
        </div>
        <div class="info-row">
          <span class="info-label">التاريخ:</span>
          <span class="info-value">${today}</span>
        </div>
        <div class="info-row">
          <span class="info-label">اسم المريض:</span>
          <span class="info-value">${escapeHtml(patient.name)} ${escapeHtml(patient.last_name || "")}</span>
        </div>
        <div class="info-row">
          <span class="info-label">رقم الهاتف:</span>
          <span class="info-value">${escapeHtml(patient.phone)}</span>
        </div>
        ${patient.medical_id ? `
        <div class="info-row">
          <span class="info-label">الرقم الطبي:</span>
          <span class="info-value">${escapeHtml(patient.medical_id)}</span>
        </div>
        ` : ''}
        
        <div class="amount-box">
          <div class="amount-row">
            <span class="info-label">المبلغ الإجمالي:</span>
            <span class="info-value">${(patient.price || 0).toLocaleString()} ${currency}</span>
          </div>
          <div class="amount-row">
            <span class="info-label">المبلغ المدفوع:</span>
            <span class="info-value" style="color:#10B981">${(patient.total_paid || 0).toLocaleString()} ${currency}</span>
          </div>
          <div class="amount-row total">
            <span class="info-label">المتبقي:</span>
            <span class="info-value remaining">${(patient.remaining_amount || 0).toLocaleString()} ${currency}</span>
          </div>
        </div>
        
        <div class="signatures">
          <div class="sig-box">
            <div class="sig-line"></div>
            <span>توقيع المريض</span>
          </div>
          <div class="sig-box">
            <div class="sig-line"></div>
            <span>ختم العيادة</span>
          </div>
        </div>
        
        <div class="footer">
          <p>هذا إيصال إلكتروني</p>
          <p>شكراً لاختياركم المركز الوطني التركي</p>
        </div>
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    
    openPrintWindow(printContent);
    toast.success("تم فتح إيصال الدفع");
  };

  // Export patient as ZIP
  const exportPatientZip = async () => {
    if (!patient) return;
    
    toast.loading("جاري تجهيز الملفات...");
    
    try {
      const JSZip = (await loadJSZip()).default;
      const { saveAs } = await loadFileSaver();
      const zip = new JSZip();
      let patientInfo = `المركز الوطني التركي لزراعة الشعر\n`;
      // Create patient info text file
      patientInfo += `========================================\n\n`;
      patientInfo += `معلومات المريض\n`;
      patientInfo += `---------------\n`;
      patientInfo += `الاسم: ${patient.name} ${patient.last_name || ""}\n`;
      patientInfo += `الهاتف: ${patient.phone}\n`;
      patientInfo += `تاريخ الميلاد: ${patient.dob ? formatDateArabic(patient.dob) : "-"}\n`;
      patientInfo += `العمر: ${patient.dob ? calculateAge(patient.dob) + " سنة" : "-"}\n`;
      patientInfo += `الجنس: ${patient.gender === "male" ? "ذكر" : "أنثى"}\n`;
      patientInfo += `العنوان: ${patient.address || "-"}\n`;
      patientInfo += `نوع المريض: ${patient.patient_type === "center" ? "مريض المركز" : patient.patient_type === "hall_rental" ? "تأجير صالة" : "-"}\n\n`;
      
      patientInfo += `المعلومات الطبية\n`;
      patientInfo += `---------------\n`;
      patientInfo += `فصيلة الدم: ${patient.blood_type || "-"}\n`;
      patientInfo += `عمليات سابقة: ${patient.previous_surgery === "yes" ? "نعم - " + (patient.previous_surgery_details || "") : "لا"}\n`;
      patientInfo += `حساسية: ${patient.has_allergies === "yes" ? "نعم - " + (patient.allergies || "") : "لا"}\n`;
      patientInfo += `أدوية: ${patient.has_medications === "yes" ? "نعم - " + (patient.medications || "") : "لا"}\n\n`;
      
      patientInfo += `معلومات العملية\n`;
      patientInfo += `---------------\n`;
      patientInfo += `تاريخ الزراعة: ${patient.transplant_date || "-"}\n`;
      patientInfo += `عدد البصيلات: ${patient.grafts_count || "-"}\n`;
      patientInfo += `الاقتطاف: ${patient.extraction_by || "-"}\n`;
      patientInfo += `فتح القنوات: ${patient.channel_opening_by || "-"}\n\n`;
      
      patientInfo += `المدفوعات\n`;
      patientInfo += `---------\n`;
      const currency = patient.currency === "USD" ? "$" : "IQD";
      patientInfo += `السعر الإجمالي: ${patient.price || 0} ${currency}\n`;
      patientInfo += `المدفوع: ${patient.total_paid || 0} ${currency}\n`;
      patientInfo += `المتبقي: ${patient.remaining_amount || 0} ${currency}\n\n`;
      
      patientInfo += `الجلسات (${sessions.length})\n`;
      patientInfo += `---------\n`;
      sessions.forEach((s, i) => {
        patientInfo += `${i + 1}. ${s.date} - ${sessionTypeLabels[s.session_type] || s.session_type} - ${s.cost_type === "free" ? "مجانية" : s.cost}\n`;
      });
      
      zip.file("patient_info.txt", patientInfo);
      
      // Add photos
      if (photos.length > 0) {
        const photosFolder = zip.folder("photos");
        photos.forEach((photo, index) => {
          const base64Data = photo.image_data.split(',')[1];
          const photoType = photoTypeLabels[photo.photo_type] || photo.photo_type;
          photosFolder.file(`${index + 1}_${photoType}_${photo.id.slice(0, 8)}.jpg`, base64Data, { base64: true });
        });
      }
      
      // Generate ZIP
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `patient_${patient.name}_${new Date().toISOString().split('T')[0]}.zip`);
      
      toast.dismiss();
      toast.success("تم تصدير ملف المريض بنجاح");
    } catch (error) {
      toast.dismiss();
      toast.error("خطأ في تصدير الملف");
      console.error(error);
    }
  };

  // Export patient file as Word document
  const exportToWord = async () => {
    try {
      toast.loading("جاري إنشاء ملف Word...");
      
      const sessionTypeLabels = { 
        followup: "مراجعة", 
        plasma: "جلسة بلازما", 
        meso: "جلسة ميزو", 
        plasma_meso: "جلسة بلازما وميزو", 
        follow_up: "متابعة",
        other: "أخرى"
      };
      
      const { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType, BorderStyle } = await loadDocx();
      const { saveAs } = await loadFileSaver();
      const currency = patient.currency === "USD" ? "$" : "IQD";
      
      const doc = new Document({
        sections: [{
          properties: { page: { margin: { top: 720, right: 720, bottom: 720, left: 720 } } },
          children: [
            new Paragraph({
              children: [new TextRun({ text: "المركز الوطني التركي لزراعة الشعر", bold: true, size: 32 })],
              alignment: AlignmentType.CENTER, spacing: { after: 200 }
            }),
            new Paragraph({
              children: [new TextRun({ text: `ملف المريض: ${patient.name}`, bold: true, size: 28 })],
              alignment: AlignmentType.CENTER, spacing: { after: 400 }
            }),
            new Paragraph({ children: [new TextRun({ text: "المعلومات الشخصية", bold: true, size: 24 })], spacing: { before: 300, after: 200 } }),
            new Paragraph({ children: [new TextRun({ text: `الاسم: ${patient.name} ${patient.last_name || ""}` })] }),
            new Paragraph({ children: [new TextRun({ text: `الهاتف: ${patient.phone}` })] }),
            new Paragraph({ children: [new TextRun({ text: `تاريخ الميلاد: ${patient.dob || "-"}` })] }),
            new Paragraph({ children: [new TextRun({ text: `العنوان: ${patient.address || "-"}` })] }),
            new Paragraph({ children: [new TextRun({ text: "معلومات العملية", bold: true, size: 24 })], spacing: { before: 300, after: 200 } }),
            new Paragraph({ children: [new TextRun({ text: `تاريخ العملية: ${patient.operation_date || patient.transplant_date || "-"}` })] }),
            new Paragraph({ children: [new TextRun({ text: `عدد البصيلات: ${patient.grafts_count || "-"}` })] }),
            new Paragraph({ children: [new TextRun({ text: `الاقتطاف: ${patient.extraction_by || "-"}` })] }),
            new Paragraph({ children: [new TextRun({ text: `فتح القنوات: ${patient.channel_opening_by || "-"}` })] }),
            new Paragraph({ children: [new TextRun({ text: "المدفوعات", bold: true, size: 24 })], spacing: { before: 300, after: 200 } }),
            new Paragraph({ children: [new TextRun({ text: `السعر: ${patient.price || 0} ${currency}` })] }),
            new Paragraph({ children: [new TextRun({ text: `المدفوع: ${patient.total_paid || 0} ${currency}` })] }),
            new Paragraph({ children: [new TextRun({ text: `المتبقي: ${patient.remaining_amount || 0} ${currency}` })] }),
            new Paragraph({ children: [new TextRun({ text: `الجلسات (${sessions.length})`, bold: true, size: 24 })], spacing: { before: 300, after: 200 } }),
            ...sessions.map(s => new Paragraph({ 
              children: [new TextRun({ text: `• ${s.date} - ${sessionTypeLabels[s.session_type] || s.session_type} - ${s.cost_type === "free" ? "مجانية" : (s.cost || 0)}` })] 
            })),
            new Paragraph({ children: [new TextRun({ text: `تاريخ التصدير: ${new Date().toLocaleString('en-GB')}`, size: 18, color: "888888" })], spacing: { before: 400 }, alignment: AlignmentType.CENTER })
          ]
        }]
      });
      
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `patient_${patient.name}_${new Date().toISOString().split('T')[0]}.docx`);
      
      toast.dismiss();
      toast.success("تم تصدير ملف Word بنجاح");
    } catch (error) {
      toast.dismiss();
      toast.error("خطأ في إنشاء ملف Word");
      console.error(error);
    }
  };

  const sessionTypeLabels = { 
    followup: "مراجعة", 
    plasma: "جلسة بلازما", 
    meso: "جلسة ميزو", 
    plasma_meso: "جلسة بلازما وميزو", 
    follow_up: "متابعة",
    other: "أخرى"
  };
  const photoTypeLabels = { before: "قبل", during: "أثناء", after: "بعد" };

  // Export patient file as PDF
  const exportToPDF = async () => {
    if (!patient) return;
    toast.info("جاري التحضير...");
    const { jsPDF } = await loadJsPDF();
    
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Add Arabic font support - use built-in helvetica for now
    doc.setFont("helvetica");
    
    // Title - Clinic Name
    doc.setFontSize(18);
    doc.setTextColor(33, 37, 41);
    doc.text("Turkish National Center", 105, 15, { align: "center" });
    doc.setFontSize(12);
    doc.text("Hair Transplant & Cosmetics", 105, 22, { align: "center" });
    
    // Line separator
    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(0.5);
    doc.line(15, 28, 195, 28);
    
    // Patient File Title
    doc.setFontSize(14);
    doc.setTextColor(100, 100, 100);
    doc.text("Patient File", 105, 36, { align: "center" });
    
    // Patient Name
    doc.setFontSize(16);
    doc.setTextColor(59, 130, 246);
    doc.text(`${patient.name} ${patient.last_name || ""}`, 105, 46, { align: "center" });
    
    let yPos = 58;
    
    // Personal Information Section
    doc.setFontSize(14);
    doc.setTextColor(33, 37, 41);
    doc.text("Personal Information", 15, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    const personalInfo = [
      ["Phone", patient.phone || "-"],
      ["Date of Birth", patient.dob || "-"],
      ["Age", patient.dob ? `${calculateAge(patient.dob)} years` : "-"],
      ["Gender", patient.gender === "male" ? "Male" : "Female"],
      ["Occupation", patient.occupation || "-"],
      ["Blood Type", patient.blood_type || "-"]
    ];
    
    personalInfo.forEach(([label, value]) => {
      doc.text(`${label}: ${value}`, 20, yPos);
      yPos += 6;
    });
    
    yPos += 5;
    
    // Medical Information Section
    doc.setFontSize(14);
    doc.setTextColor(33, 37, 41);
    doc.text("Medical Information", 15, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    const medicalInfo = [
      ["Previous Surgery", patient.previous_surgery === "yes" ? "Yes" : patient.previous_surgery === "no" ? "No" : "-"],
      ["Allergies", patient.allergies || "None"],
      ["Medications", patient.medications || "None"],
      ["Smoking", patient.smoking === "yes" ? "Yes" : patient.smoking === "no" ? "No" : patient.smoking === "former" ? "Former" : "-"],
      ["Blood Pressure", patient.blood_pressure === "normal" ? "Normal" : patient.blood_pressure === "high" ? "High" : patient.blood_pressure === "low" ? "Low" : "-"],
      ["Diabetes", patient.diabetes === "yes" ? "Yes" : patient.diabetes === "no" ? "No" : "-"]
    ];
    
    medicalInfo.forEach(([label, value]) => {
      doc.text(`${label}: ${value}`, 20, yPos);
      yPos += 6;
    });
    
    yPos += 5;
    
    // Operation Information Section
    doc.setFontSize(14);
    doc.setTextColor(33, 37, 41);
    doc.text("Operation Details", 15, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    const operationInfo = [
      ["Grafts Count", patient.grafts_count || "-"],
      ["Extraction By", patient.extraction_by || "-"],
      ["Channel Opening By", patient.channel_opening_by || "-"],
      ["Planting 1", patient.transplant_1 || "-"],
      ["Planting 2", patient.transplant_2 || "-"],
      ["Price", patient.price ? `${patient.price}` : "-"]
    ];
    
    operationInfo.forEach(([label, value]) => {
      doc.text(`${label}: ${value}`, 20, yPos);
      yPos += 6;
    });
    
    // Sessions Table
    if (sessions.length > 0) {
      yPos += 10;
      doc.setFontSize(14);
      doc.setTextColor(33, 37, 41);
      doc.text("Sessions History", 15, yPos);
      yPos += 5;
      
      const sessionsData = sessions.map(s => [
        s.date,
        sessionTypeLabels[s.session_type] || s.session_type,
        s.grafts_count || "-",
        s.cost || "-",
        s.notes || "-"
      ]);
      
      doc.autoTable({
        startY: yPos,
        head: [["Date", "Type", "Grafts", "Cost", "Notes"]],
        body: sessionsData,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 15, right: 15 }
      });
    }
    
    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Generated on ${new Date().toLocaleDateString()}`, 15, 285);
      doc.text(`Page ${i} of ${pageCount}`, 195, 285, { align: "right" });
    }
    
    // Save PDF
    doc.save(`patient_${patient.name}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("تم تصدير الملف بنجاح");
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse-soft text-gray-500">جاري التحميل...</div></div>;
  }

  if (!patient) {
    return <div className="empty-state"><h3>المريض غير موجود</h3></div>;
  }

  return (
    <div className="animate-fade-in" data-testid="patient-detail-page">
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <button onClick={() => navigate("/patients")} className="btn btn-secondary p-2" data-testid="back-btn">
          <ChevronLeft className="w-5 h-5 rtl-flip" />
        </button>
        <div className="flex-1 min-w-[200px]">
          <h1 className="page-title flex items-center gap-2">
            {patient.name}
            {patient.patient_type === "hall_rental" && (
              <span className="text-sm px-2 py-1 bg-amber-100 text-amber-700 rounded-full">تأجير</span>
            )}
          </h1>
          <p className="page-subtitle">ملف المريض</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => navigate(`/patients?edit=${patient.id}`)} className="btn btn-secondary" data-testid="edit-patient-btn" title="تعديل البيانات">
            <Edit className="w-5 h-5" />
            <span className="hidden sm:inline">تعديل</span>
          </button>
          <button onClick={printPaymentReceipt} className="btn btn-secondary" data-testid="print-receipt-btn" title="طباعة وصل دفع">
            <Printer className="w-5 h-5" />
            <span className="hidden sm:inline">وصل دفع</span>
          </button>
          <button onClick={exportPatientZip} className="btn btn-secondary" data-testid="export-zip-btn" title="تصدير ZIP">
            <Archive className="w-5 h-5" />
            <span className="hidden sm:inline">ZIP</span>
          </button>
          <button onClick={exportToWord} className="btn btn-secondary" data-testid="export-word-btn" title="تصدير Word">
            <FileText className="w-5 h-5" />
            <span className="hidden sm:inline">Word</span>
          </button>
          <button onClick={exportToPDF} className="btn btn-primary" data-testid="export-pdf-btn" title="تصدير PDF">
            <Download className="w-5 h-5" />
            <span className="hidden sm:inline">PDF</span>
          </button>
          {isAdmin() && (
          <button onClick={deletePatient} className="btn btn-danger" data-testid="delete-patient-btn" title="حذف">
            <Trash2 className="w-5 h-5" />
          </button>
          )}
        </div>
      </div>

      {/* Package Sessions Tracking */}
      {(patient.package_plasma_sessions || patient.package_meso_sessions) && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4 mb-4">
          <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Activity className="w-5 h-5 text-purple-600" />
            تتبع جلسات الباقة
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {patient.package_plasma_sessions > 0 && (() => {
              const plasmaSessions = sessions.filter(s => s.session_type === "plasma" || s.session_type === "plasma_meso");
              const maxPlasmaNum = plasmaSessions.length > 0 
                ? Math.max(...plasmaSessions.map(s => s.session_number || 0), plasmaSessions.length)
                : 0;
              const remaining = Math.max(0, patient.package_plasma_sessions - maxPlasmaNum);
              return (
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">جلسات البلازما</p>
                  <p className="text-lg font-bold text-purple-600">
                    {maxPlasmaNum}
                    <span className="text-gray-400 text-sm"> / {patient.package_plasma_sessions}</span>
                  </p>
                  <p className="text-xs text-green-600 mt-1">المتبقي: {remaining}</p>
                  <div className="mt-2 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-500 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min(100, (maxPlasmaNum / patient.package_plasma_sessions) * 100)}%` }}
                    />
                  </div>
                </div>
              );
            })()}
            {patient.package_meso_sessions > 0 && (() => {
              const mesoSessions = sessions.filter(s => s.session_type === "meso" || s.session_type === "plasma_meso");
              const maxMesoNum = mesoSessions.length > 0
                ? Math.max(...mesoSessions.map(s => s.session_number || 0), mesoSessions.length)
                : 0;
              const remaining = Math.max(0, patient.package_meso_sessions - maxMesoNum);
              return (
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">جلسات الميزو</p>
                  <p className="text-lg font-bold text-pink-600">
                    {maxMesoNum}
                    <span className="text-gray-400 text-sm"> / {patient.package_meso_sessions}</span>
                  </p>
                  <p className="text-xs text-green-600 mt-1">المتبقي: {remaining}</p>
                  <div className="mt-2 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-pink-500 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min(100, (maxMesoNum / patient.package_meso_sessions) * 100)}%` }}
                    />
                  </div>
                </div>
              );
            })()}
            <div className="bg-white rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 mb-1">شامبو</p>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${
                patient.shampoo_delivered ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
              }`}>
                {patient.shampoo_delivered ? <CheckCircle className="w-4 h-4" /> : null}
                {patient.shampoo_delivered ? "تم التسليم" : "لم يسلم"}
              </span>
            </div>
            <div className="bg-white rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 mb-1">لوشن + فيتامين</p>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${
                patient.lotion_delivered && patient.vitamins_delivered ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
              }`}>
                {patient.lotion_delivered && patient.vitamins_delivered ? <CheckCircle className="w-4 h-4" /> : null}
                {patient.lotion_delivered && patient.vitamins_delivered ? "تم التسليم" : "لم يسلم"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Payment Summary Card */}
      {(patient.price || patient.total_paid) && (
        <div className="bg-gradient-to-r from-blue-50 to-emerald-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Wallet className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-gray-500">الحالة المالية</p>
                <p className="font-bold text-lg text-gray-800">
                  {patient.remaining_amount > 0 ? "متبقي مبلغ" : "مسدد بالكامل"}
                </p>
              </div>
            </div>
            <div className="flex gap-6 text-center">
              <div>
                <p className="text-xs text-gray-500">الإجمالي</p>
                <p className="font-bold text-gray-800">{patient.price || 0} {patient.currency === "USD" ? "$" : "IQD"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">المدفوع</p>
                <p className="font-bold text-emerald-600">{patient.total_paid || 0}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">المتبقي</p>
                <p className={`font-bold ${patient.remaining_amount > 0 ? "text-red-600" : "text-emerald-600"}`}>
                  {patient.remaining_amount || 0}
                </p>
              </div>
            </div>
          </div>
          {patient.payment_type === "installment" && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">أقساط</span>
                {patient.installment_company && <span className="text-sm text-gray-600">الشركة: <strong>{patient.installment_company}</strong></span>}
                {patient.installment_amount > 0 && <span className="text-sm text-gray-600">خصم الشركة: <strong className="text-red-600">{patient.installment_amount?.toLocaleString()}</strong></span>}
                {patient.price && patient.installment_amount > 0 && (
                  <span className="text-sm text-gray-600">المتوفر: <strong className="text-emerald-600">{(patient.price - patient.installment_amount).toLocaleString()}</strong></span>
                )}
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${patient.installment_status === "received" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                  {patient.installment_status === "received" ? "تم الاستلام" : "بانتظار التحويل"}
                </span>
                {patient.installment_status === "received" && patient.installment_received_date && (
                  <span className="text-xs text-emerald-600">استلم بتاريخ: {patient.installment_received_date}</span>
                )}
                {patient.installment_expected_date && patient.installment_status !== "received" && (
                  <span className="text-xs text-gray-500">متوقع: {patient.installment_expected_date}</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="tabs">
        {["info", "sessions", "photos", "complaints"].map((tab) => (
          <button
            key={tab}
            className={`tab ${activeTab === tab ? "active" : ""}`}
            onClick={() => setActiveTab(tab)}
            data-testid={`tab-${tab}`}
          >
            {tab === "info" ? "المعلومات" : tab === "sessions" ? "الجلسات" : tab === "photos" ? "الصور" : "الشكاوي"}
          </button>
        ))}
      </div>

      {activeTab === "info" && (
        <div className="data-table animate-slide-in">
          <div className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="patient-avatar w-20 h-20 text-2xl">
                {patient.name.charAt(0)}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{patient.name} {patient.last_name || ""}</h2>
                <p className="text-gray-500">{patient.gender === "male" ? "ذكر" : "أنثى"}{patient.dob && ` - ${calculateAge(patient.dob)} سنة`}{patient.occupation ? ` - ${patient.occupation}` : ""}</p>
              </div>
              {/* WhatsApp Button */}
              {patient.phone && (
                <button
                  onClick={() => setShowWhatsAppModal(true)}
                  className="mr-auto btn bg-green-500 hover:bg-green-600 text-white flex items-center gap-2"
                  title="إرسال رسالة WhatsApp"
                  data-testid="whatsapp-btn"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </button>
              )}
            </div>

            {/* المعلومات الشخصية */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 mb-3 pb-2 border-b">المعلومات الشخصية</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-gray-500">رقم الهاتف</label>
                  <p className="font-medium flex items-center gap-2">
                    <Phone className="w-4 h-4 text-blue-500" />
                    {patient.phone}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">تاريخ الميلاد</label>
                  <p className="font-medium flex items-center gap-2">
                    <Cake className="w-4 h-4 text-pink-500" />
                    {patient.dob ? formatDateArabic(patient.dob) : "غير محدد"}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">العمر</label>
                  <p className="font-medium">{patient.dob ? `${calculateAge(patient.dob)} سنة` : "غير محدد"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">الوظيفة</label>
                  <p className="font-medium">{patient.occupation || "غير محدد"}</p>
                </div>
              </div>
            </div>

            {/* المعلومات الطبية */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 mb-3 pb-2 border-b">المعلومات الطبية</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-gray-500">فصيلة الدم</label>
                  <p className="font-medium">{patient.blood_type || "غير محدد"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">عملية سابقة</label>
                  <p className="font-medium">{patient.previous_surgery === "yes" ? "نعم" : patient.previous_surgery === "no" ? "لا" : "غير محدد"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">الحساسية</label>
                  <p className="font-medium">{patient.allergies || "لا يوجد"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">الأدوية</label>
                  <p className="font-medium">{patient.medications || "لا يوجد"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">التدخين</label>
                  <p className="font-medium">{patient.smoking === "yes" ? "نعم" : patient.smoking === "no" ? "لا" : patient.smoking === "former" ? "سابقاً" : "غير محدد"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">الكحول</label>
                  <p className="font-medium">{patient.alcohol === "yes" ? "نعم" : patient.alcohol === "no" ? "لا" : patient.alcohol === "former" ? "سابقاً" : "غير محدد"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">ضغط الدم</label>
                  <p className="font-medium">{patient.blood_pressure === "normal" ? "طبيعي" : patient.blood_pressure === "high" ? "مرتفع" : patient.blood_pressure === "low" ? "منخفض" : "غير محدد"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">السكري</label>
                  <p className="font-medium">{patient.diabetes === "yes" ? "نعم" : patient.diabetes === "no" ? "لا" : patient.diabetes === "prediabetes" ? "مقدمات" : "غير محدد"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">سبب التساقط</label>
                  <p className="font-medium">{patient.hair_loss_cause === "genetic" ? "وراثي" : patient.hair_loss_cause === "vitamin_deficiency" ? "نقص فيتامينات" : patient.hair_loss_cause === "psychological" ? "حالة نفسية" : patient.hair_loss_cause === "other" ? "غير ذلك" : "غير محدد"}</p>
                </div>
              </div>
              {patient.medical_history && (
                <div className="mt-4">
                  <label className="text-sm text-gray-500">التاريخ الطبي</label>
                  <p className="font-medium">{patient.medical_history}</p>
                </div>
              )}
            </div>

            {/* معلومات العملية */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 mb-3 pb-2 border-b">معلومات العملية</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-gray-500">نوع المريض</label>
                  <p className="font-medium">{patient.patient_type === "center" ? "مريض المركز" : patient.patient_type === "hall_rental" ? "تأجير صالة" : "غير محدد"}</p>
                </div>
                {patient.hall_rental_from && (
                  <div>
                    <label className="text-sm text-gray-500">تأجير من</label>
                    <p className="font-medium">{patient.hall_rental_from}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm text-gray-500">كادر الزراعة</label>
                  <p className="font-medium">{patient.transplant_team || "غير محدد"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">الاقتطاف</label>
                  <p className="font-medium">{patient.extraction_by || "غير محدد"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">فتح القنوات</label>
                  <p className="font-medium">{patient.channel_opening_by || "غير محدد"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">زراعة 1</label>
                  <p className="font-medium">{patient.transplant_1 || "غير محدد"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">زراعة 2</label>
                  <p className="font-medium">{patient.transplant_2 || "غير محدد"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">عدد البصيلات</label>
                  <p className="font-medium">{patient.grafts_count || "غير محدد"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">نوع الشعر</label>
                  <p className="font-medium">{patient.hair_type === "fine" ? "خفيف" : patient.hair_type === "normal" ? "عادي" : patient.hair_type === "coarse" ? "خشن" : patient.hair_type === "curly" ? "مجعد" : "غير محدد"}</p>
                </div>
              </div>
            </div>

            {/* التواريخ */}
            <div className="bg-gray-50 rounded-xl p-3 mt-4">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <label className="text-gray-400">تاريخ الإضافة</label>
                  <p className="font-medium text-gray-600">{formatFullDateTime(patient.created_at)}</p>
                </div>
                {patient.updated_at && (
                <div>
                  <label className="text-gray-400">آخر تعديل</label>
                  <p className="font-medium text-gray-600">{formatFullDateTime(patient.updated_at)}</p>
                </div>
                )}
              </div>
            </div>

            {/* معلومات الحجز */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 mb-3 pb-2 border-b">الحجز والتسعير</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-gray-500">الزبون عن طريق</label>
                  <p className="font-medium">{patient.referral_source || "غير محدد"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">الحجز عن طريق</label>
                  <p className="font-medium">{patient.booking_source || "غير محدد"}</p>
                </div>
                {isAdmin() && (
                <div>
                  <label className="text-sm text-gray-500">السعر</label>
                  <p className="font-medium text-emerald-600">{patient.price ? `${patient.price}` : "غير محدد"}</p>
                </div>
                )}
                {patient.next_followup_date && (
                  <div>
                    <label className="text-sm text-gray-500">المراجعة القادمة</label>
                    <p className="font-medium flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      {patient.next_followup_date}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* ملاحظات */}
            {patient.notes && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-3 pb-2 border-b">ملاحظات</h3>
                <p className="text-gray-600">{patient.notes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "sessions" && (
        <div className="animate-slide-in">
          {/* Session Progress Visual */}
          {(patient.package_plasma_sessions > 0 || patient.package_meso_sessions > 0) && (() => {
            const before = patient.completed_sessions_before || 0;
            const plasmaTotal = patient.package_plasma_sessions || 0;
            const mesoTotal = patient.package_meso_sessions || 0;
            const maxTotal = Math.max(plasmaTotal, mesoTotal);
            const plasmaDone = sessions.filter(s => s.session_type === "plasma" || s.session_type === "plasma_meso").length;
            const mesoDone = sessions.filter(s => s.session_type === "meso" || s.session_type === "plasma_meso").length;
            const plasmaCurrent = before + plasmaDone;
            const mesoCurrent = before + mesoDone;
            const isComplete = patient.sessions_status === "completed";

            const setReached = async (num) => {
              try {
                await api.post(`/patients/${patient.id}/set-session-progress`, { 
                  completed_sessions_before: num, 
                  sessions_status: num >= maxTotal ? "completed" : "ongoing" 
                }, token);
                toast.success(`تم: وصل للجلسة رقم ${num}`);
                fetchData();
              } catch (err) { console.error(err); toast.error("خطأ"); }
            };

            const markComplete = async () => {
              if (!window.confirm("تأكيد: أكمل جميع الجلسات؟")) return;
              try {
                await api.post(`/patients/${patient.id}/set-session-progress`, { 
                  completed_sessions_before: maxTotal, 
                  sessions_status: "completed" 
                }, token);
                toast.success("تم: أكمل جميع الجلسات");
                fetchData();
              } catch (err) { console.error(err); toast.error("خطأ"); }
            };

            return (
              <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4" data-testid="session-progress-card">
                <div className="flex items-center justify-between mb-5">
                  <h4 className="font-bold text-gray-800 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-purple-600" />
                    تقدم الجلسات
                  </h4>
                  {!isComplete ? (
                    <button onClick={markComplete}
                      className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 transition-colors"
                      data-testid="complete-all-sessions-btn">
                      <CheckCircle className="w-4 h-4" /> أكمل جميع الجلسات
                    </button>
                  ) : (
                    <span className="flex items-center gap-1.5 px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-bold">
                      <CheckCircle className="w-4 h-4" /> مكتمل
                    </span>
                  )}
                </div>

                {/* Quick session number selector */}
                {!isComplete && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5" data-testid="quick-session-set">
                    <p className="text-sm font-bold text-amber-800 mb-3">وصل للجلسة رقم كم؟</p>
                    <div className="flex items-center gap-2 flex-wrap" dir="ltr">
                      {Array.from({ length: maxTotal }, (_, i) => {
                        const num = i + 1;
                        return (
                          <button key={num} onClick={() => setReached(num)}
                            className={`w-11 h-11 rounded-xl font-bold text-sm transition-all ${
                              num <= before ? "bg-purple-500 text-white shadow-md ring-2 ring-purple-300" :
                              num <= (before + Math.max(plasmaDone, mesoDone)) ? "bg-blue-500 text-white shadow-md" :
                              "bg-white border-2 border-gray-300 text-gray-600 hover:border-amber-400 hover:bg-amber-50 hover:text-amber-700"
                            }`}
                            data-testid={`set-session-${num}`}>
                            {num}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex gap-4 mt-2 text-[11px]">
                      <span className="text-purple-600 flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-500 inline-block" /> قبل النظام</span>
                      {Math.max(plasmaDone, mesoDone) > 0 && <span className="text-blue-600 flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500 inline-block" /> بالنظام</span>}
                      <span className="text-gray-400 flex items-center gap-1"><span className="w-3 h-3 rounded bg-white border-2 border-gray-300 inline-block" /> لم تكتمل</span>
                    </div>
                  </div>
                )}

                {/* Plasma Progress Bar */}
                {plasmaTotal > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">بلازما</span>
                      <span className="text-sm">
                        <span className="font-bold text-purple-600">{plasmaCurrent}</span>
                        <span className="text-gray-400"> / {plasmaTotal}</span>
                        {plasmaTotal - plasmaCurrent > 0 && <span className="text-red-500 text-xs mr-2"> (متبقي {plasmaTotal - plasmaCurrent})</span>}
                      </span>
                    </div>
                    <div className="flex gap-1" dir="ltr">
                      {Array.from({ length: plasmaTotal }, (_, i) => (
                        <div key={i} className={`flex-1 h-9 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${
                          i < before ? "bg-gray-300 text-gray-600" :
                          i < plasmaCurrent ? "bg-purple-500 text-white" :
                          "bg-gray-100 text-gray-400 border border-dashed border-gray-300"
                        }`}>{i + 1}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Meso Progress Bar */}
                {mesoTotal > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">ميزوثيرابي</span>
                      <span className="text-sm">
                        <span className="font-bold text-pink-600">{mesoCurrent}</span>
                        <span className="text-gray-400"> / {mesoTotal}</span>
                        {mesoTotal - mesoCurrent > 0 && <span className="text-red-500 text-xs mr-2"> (متبقي {mesoTotal - mesoCurrent})</span>}
                      </span>
                    </div>
                    <div className="flex gap-1" dir="ltr">
                      {Array.from({ length: mesoTotal }, (_, i) => (
                        <div key={i} className={`flex-1 h-9 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${
                          i < before ? "bg-gray-300 text-gray-600" :
                          i < mesoCurrent ? "bg-pink-500 text-white" :
                          "bg-gray-100 text-gray-400 border border-dashed border-gray-300"
                        }`}>{i + 1}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-900">الجلسات ({(patient.completed_sessions_before || 0) + sessions.length})</h3>
            <button onClick={() => setShowSessionModal(true)} className="btn btn-primary" data-testid="add-session-btn">
              <Plus className="w-5 h-5" />
              إضافة جلسة
            </button>
          </div>

          {sessions.length === 0 ? (
            <div className="empty-state data-table">
              <FileText className="empty-state-icon" />
              <h3>لا توجد جلسات</h3>
              <p>أضف جلسة جديدة للمريض</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sessions.map((session) => (
                <div key={session.id} className="data-table p-4" data-testid={`session-${session.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                          session.session_type === "plasma" || session.session_type === "plasma_meso" ? "badge-success" :
                          session.session_type === "meso" ? "badge-info" : "badge-warning"
                        }`}>
                          {sessionTypeLabels[session.session_type] || session.session_type}
                        </span>
                        {session.session_number && (
                          <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                            جلسة #{session.session_number}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600">{session.date}</p>
                      {session.notes && <p className="text-sm text-gray-500 mt-2">{session.notes}</p>}
                      {session.next_review_date && (
                        <p className="text-sm text-blue-600 mt-1 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          المراجعة القادمة: {session.next_review_date}
                        </p>
                      )}
                    </div>
                    <div className="text-left">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        session.cost_type === "free" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                      }`}>
                        {session.cost_type === "free" ? "مجانية" : `${session.cost || 0}`}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "photos" && (
        <div className="animate-slide-in">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-900">الصور ({photos.length})</h3>
            <button onClick={() => setShowPhotoModal(true)} className="btn btn-primary" data-testid="add-photo-btn">
              <Camera className="w-5 h-5" />
              إضافة صورة
            </button>
          </div>

          {photos.length === 0 ? (
            <div className="empty-state data-table">
              <Image className="empty-state-icon" />
              <h3>لا توجد صور</h3>
              <p>أضف صور للمريض لتوثيق التقدم</p>
            </div>
          ) : (
            <div className="photo-grid">
              {photos.map((photo) => (
                <div 
                  key={photo.id} 
                  className="relative group cursor-pointer" 
                  data-testid={`photo-${photo.id}`}
                  onClick={() => setEnlargedPhoto(photo)}
                >
                  <div className="photo-item hover:scale-105 transition-transform">
                    <img 
                      src={resolveUrl(photo.thumbnail_url || photo.image_url) || photo.image_data} 
                      alt={photo.description || "صورة المريض"} 
                      loading="lazy"
                      decoding="async"
                      style={{ backgroundColor: '#f3f4f6' }}
                    />
                  </div>
                  <div className="absolute top-2 right-2">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                      photo.photo_type === "before" ? "badge-info" :
                      photo.photo_type === "during" ? "badge-warning" : "badge-success"
                    }`}>
                      {photoTypeLabels[photo.photo_type]}
                    </span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); deletePhoto(photo.id); }}
                    className="absolute top-2 left-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-photo-${photo.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {photo.description && (
                    <p className="text-xs text-gray-500 mt-2 text-center">{photo.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Photo Modal */}
      {showPhotoModal && (
        <div className="modal-overlay" onClick={() => { if (!isUploading) { setShowPhotoModal(false); setMultiplePhotos([]); } }}>
          <div className="modal-content max-w-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">إضافة صور</h2>
              <button onClick={() => { if (!isUploading) { setShowPhotoModal(false); setMultiplePhotos([]); } }} className="p-2 hover:bg-gray-100 rounded-lg" disabled={isUploading}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">نوع الصور</label>
                <select
                  className="form-input"
                  value={photoData.photo_type}
                  onChange={(e) => setPhotoData({ ...photoData, photo_type: e.target.value })}
                  data-testid="photo-type-select"
                  disabled={isUploading}
                >
                  <option value="before">قبل</option>
                  <option value="during">أثناء</option>
                  <option value="after">بعد</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">الصور (يمكنك اختيار عدة صور)</label>
                <div className="photo-upload border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="photo-upload"
                    data-testid="photo-upload-input"
                    disabled={isUploading}
                  />
                  <label htmlFor="photo-upload" className="cursor-pointer block">
                    {multiplePhotos.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {multiplePhotos.map((photo, index) => (
                          <div key={index} className="relative group">
                            <img src={photo.data} alt={`Preview ${index + 1}`} className="w-full h-24 object-cover rounded-lg" />
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); removePhoto(index); }}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        <div className="w-full h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-400">
                          <Plus className="w-6 h-6" />
                        </div>
                      </div>
                    ) : (
                      <>
                        <Camera className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                        <p className="text-gray-500">اضغط لاختيار صورة أو أكثر</p>
                        <p className="text-xs text-gray-400 mt-1">يمكنك اختيار عدة صور دفعة واحدة</p>
                      </>
                    )}
                  </label>
                </div>
                {multiplePhotos.length > 0 && (
                  <p className="text-sm text-blue-600 mt-2">تم اختيار {multiplePhotos.length} صورة</p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">وصف (اختياري)</label>
                <input
                  type="text"
                  className="form-input"
                  value={photoData.description}
                  onChange={(e) => setPhotoData({ ...photoData, description: e.target.value })}
                  data-testid="photo-description-input"
                  disabled={isUploading}
                />
              </div>
              {isUploading && (
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>جاري الرفع...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                onClick={submitPhoto} 
                className="btn btn-primary" 
                data-testid="save-photo-btn"
                disabled={isUploading || (multiplePhotos.length === 0 && !photoData.image_data)}
              >
                {isUploading ? "جاري الرفع..." : `رفع ${multiplePhotos.length > 1 ? multiplePhotos.length + " صور" : "الصورة"}`}
              </button>
              <button 
                onClick={() => { setShowPhotoModal(false); setMultiplePhotos([]); }} 
                className="btn btn-secondary"
                disabled={isUploading}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Modal */}
      {showSessionModal && (
        <div className="modal-overlay" onClick={() => setShowSessionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">إضافة جلسة</h2>
              <button onClick={() => setShowSessionModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">نوع الجلسة *</label>
                <select
                  className="form-input"
                  value={sessionData.session_type}
                  onChange={(e) => {
                    const type = e.target.value;
                    // حساب رقم الجلسة التالي حسب النوع
                    let nextNum = 1;
                    if (type === "plasma" || type === "plasma_meso") {
                      nextNum = sessions.filter(s => s.session_type === "plasma" || s.session_type === "plasma_meso").length + 1;
                    } else if (type === "meso") {
                      nextNum = sessions.filter(s => s.session_type === "meso" || s.session_type === "plasma_meso").length + 1;
                    } else {
                      nextNum = sessions.filter(s => s.session_type === type).length + 1;
                    }
                    setSessionData({ ...sessionData, session_type: type, custom_type: "", session_number: nextNum });
                  }}
                  data-testid="session-type-select"
                >
                  <option value="followup">مراجعة</option>
                  <option value="plasma">جلسة بلازما</option>
                  <option value="meso">جلسة ميزو</option>
                  <option value="plasma_meso">جلسة بلازما وميزو</option>
                  <option value="follow_up">متابعة</option>
                  <option value="other">أخرى</option>
                </select>
              </div>
              
              {/* عرض معلومات الباقة إذا كان النوع بلازما أو ميزو */}
              {(sessionData.session_type === "plasma" || sessionData.session_type === "plasma_meso") && patient.package_plasma_sessions > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-purple-700">جلسات البلازما في الباقة:</span>
                    <span className="font-bold text-purple-700">{patient.package_plasma_sessions}</span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-sm text-purple-600">المستخدمة:</span>
                    <span className="font-medium">{sessions.filter(s => s.session_type === "plasma" || s.session_type === "plasma_meso").length}</span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-sm text-green-600">المتبقية:</span>
                    <span className="font-bold text-green-600">{Math.max(0, patient.package_plasma_sessions - sessions.filter(s => s.session_type === "plasma" || s.session_type === "plasma_meso").length)}</span>
                  </div>
                </div>
              )}
              
              {(sessionData.session_type === "meso" || sessionData.session_type === "plasma_meso") && patient.package_meso_sessions > 0 && (
                <div className="bg-pink-50 border border-pink-200 rounded-lg p-3 mb-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-pink-700">جلسات الميزو في الباقة:</span>
                    <span className="font-bold text-pink-700">{patient.package_meso_sessions}</span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-sm text-pink-600">المستخدمة:</span>
                    <span className="font-medium">{sessions.filter(s => s.session_type === "meso" || s.session_type === "plasma_meso").length}</span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-sm text-green-600">المتبقية:</span>
                    <span className="font-bold text-green-600">{Math.max(0, patient.package_meso_sessions - sessions.filter(s => s.session_type === "meso" || s.session_type === "plasma_meso").length)}</span>
                  </div>
                </div>
              )}
              
              <div className="form-group">
                <label className="form-label">رقم الجلسة (من هذا النوع)</label>
                <input
                  type="number"
                  className="form-input"
                  value={sessionData.session_number || 1}
                  onChange={(e) => setSessionData({ ...sessionData, session_number: parseInt(e.target.value) || 1 })}
                  min="1"
                  data-testid="session-number-input"
                />
                <p className="text-xs text-gray-500 mt-1">مثال: إذا هذي الجلسة الرابعة من البلازما، اكتب 4</p>
              </div>
              {sessionData.session_type === "other" && (
                <div className="form-group">
                  <label className="form-label">اكتب نوع الجلسة *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={sessionData.custom_type}
                    onChange={(e) => setSessionData({ ...sessionData, custom_type: e.target.value })}
                    placeholder="أدخل نوع الجلسة"
                    data-testid="session-custom-type-input"
                  />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">تاريخ الجلسة *</label>
                <input
                  type="date"
                  className="form-input"
                  value={sessionData.date}
                  onChange={(e) => setSessionData({ ...sessionData, date: e.target.value })}
                  data-testid="session-date-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">تكلفة الجلسة</label>
                  <select
                    className="form-input"
                    value={sessionData.cost_type}
                    onChange={(e) => setSessionData({ ...sessionData, cost_type: e.target.value, cost: e.target.value === "free" ? "" : sessionData.cost })}
                    data-testid="session-cost-type-select"
                  >
                    <option value="free">مجانية</option>
                    <option value="paid">مدفوعة</option>
                  </select>
                </div>
                {sessionData.cost_type === "paid" && (
                  <div className="form-group">
                    <label className="form-label">المبلغ *</label>
                    <input
                      type="number"
                      className="form-input"
                      value={sessionData.cost}
                      onChange={(e) => setSessionData({ ...sessionData, cost: e.target.value })}
                      placeholder="0"
                      data-testid="session-cost-input"
                    />
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  موعد المراجعة القادمة
                </label>
                <input
                  type="date"
                  className="form-input"
                  value={sessionData.next_review_date}
                  onChange={(e) => setSessionData({ ...sessionData, next_review_date: e.target.value })}
                  data-testid="session-next-review-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">ملاحظات</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={sessionData.notes}
                  onChange={(e) => setSessionData({ ...sessionData, notes: e.target.value })}
                  data-testid="session-notes-input"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={submitSession} className="btn btn-primary" data-testid="save-session-btn">
                حفظ الجلسة
              </button>
              <button onClick={() => setShowSessionModal(false)} className="btn btn-secondary">
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enlarged Photo Modal */}
      {enlargedPhoto && (
        <div className="modal-overlay" onClick={() => setEnlargedPhoto(null)}>
          <div className="relative max-w-4xl max-h-[90vh] mx-auto" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setEnlargedPhoto(null)} 
              className="absolute -top-10 right-0 p-2 bg-white/20 hover:bg-white/40 rounded-full text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <img 
              src={resolveUrl(enlargedPhoto.image_url) || enlargedPhoto.image_data} 
              alt={enlargedPhoto.description || "صورة المريض"} 
              className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
            />
            <div className="absolute bottom-4 left-0 right-0 text-center">
              <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${
                enlargedPhoto.photo_type === "before" ? "bg-blue-500 text-white" :
                enlargedPhoto.photo_type === "during" ? "bg-amber-500 text-white" : "bg-green-500 text-white"
              }`}>
                {photoTypeLabels[enlargedPhoto.photo_type]}
              </span>
              {enlargedPhoto.description && (
                <p className="text-white mt-2 text-shadow">{enlargedPhoto.description}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Complaints Tab */}
      {activeTab === "complaints" && <ComplaintsSection patientId={patient.id} token={token} />}

      {/* WhatsApp Modal */}
      {showWhatsAppModal && (
        <div className="modal-overlay" onClick={() => setShowWhatsAppModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header bg-green-500 text-white rounded-t-xl">
              <h2 className="modal-title flex items-center gap-2">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                إرسال رسالة WhatsApp
              </h2>
              <button onClick={() => setShowWhatsAppModal(false)} className="text-white hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="modal-body">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4 text-right" data-testid="whatsapp-sandbox-warning">
                <p className="text-xs font-bold text-amber-800 mb-1">تنبيه: وضع Sandbox (تجريبي)</p>
                <p className="text-xs text-amber-700 mb-1">لاستقبال الرسائل، يجب على المستلم أولاً إرسال:</p>
                <p className="text-xs font-mono bg-amber-100 px-2 py-1 rounded text-center my-1 select-all" dir="ltr">join plenty-built</p>
                <p className="text-xs text-amber-700">إلى الرقم <span dir="ltr" className="font-mono">+14155238886</span> عبر WhatsApp</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg mb-4">
                <p className="text-sm text-green-800">
                  <strong>إرسال إلى:</strong> {patient?.name}
                </p>
                <p className="text-sm text-green-600">{patient?.phone}</p>
              </div>
              
              <div className="form-group">
                <label className="form-label">نوع الرسالة</label>
                <select
                  className="form-input"
                  value={whatsappReminderType}
                  onChange={(e) => setWhatsappReminderType(e.target.value)}
                  data-testid="whatsapp-type-select"
                >
                  <option value="custom">رسالة مخصصة</option>
                  <option value="appointment">تذكير بالموعد</option>
                  <option value="followup">تذكير بالمراجعة</option>
                  <option value="payment">تذكير بالدفعة</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  {whatsappReminderType === "custom" ? "نص الرسالة *" : "رسالة إضافية (اختياري)"}
                </label>
                <textarea
                  className="form-input"
                  rows={4}
                  value={whatsappMessage}
                  onChange={(e) => setWhatsappMessage(e.target.value)}
                  placeholder={whatsappReminderType === "custom" ? "اكتب رسالتك هنا..." : "سيتم إرسال رسالة تلقائية..."}
                  data-testid="whatsapp-message-input"
                />
              </div>

              {whatsappReminderType !== "custom" && (
                <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                  <strong>معاينة:</strong>
                  <p className="mt-1">
                    {whatsappReminderType === "appointment" && `مرحباً ${patient?.name} 👋 نذكرك بموعدك في المركز...`}
                    {whatsappReminderType === "followup" && `مرحباً ${patient?.name} 👋 نذكرك بموعد المراجعة...`}
                    {whatsappReminderType === "payment" && `مرحباً ${patient?.name} 👋 نود تذكيرك بالمبلغ المتبقي...`}
                  </p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                onClick={() => setShowWhatsAppModal(false)} 
                className="btn btn-secondary"
                disabled={whatsappSending}
              >
                إلغاء
              </button>
              <button 
                onClick={sendWhatsApp} 
                className="btn bg-green-500 hover:bg-green-600 text-white flex items-center gap-2"
                disabled={whatsappSending || (whatsappReminderType === "custom" && !whatsappMessage.trim())}
                data-testid="send-whatsapp-btn"
              >
                {whatsappSending ? (
                  <span className="animate-spin">⏳</span>
                ) : (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                  </svg>
                )}
                {whatsappSending ? "جاري الإرسال..." : "إرسال"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Settings Page

// Audit Log Section
const AuditLogSection = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const url = filter === "all" ? "/audit-log?limit=200" : `/audit-log?limit=200&entity_type=${filter}`;
        const res = await api.get(url, token);
        setLogs(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchLogs();
  }, [token, filter]);

  const fmtDate = (iso) => {
    if (!iso) return "";
    return formatFullDateTime(iso);
  };

  const actionColors = {
    "إضافة": "bg-emerald-100 text-emerald-700",
    "تعديل": "bg-blue-100 text-blue-700",
    "حذف": "bg-red-100 text-red-700",
    "دفع": "bg-amber-100 text-amber-700",
    "بيع": "bg-emerald-100 text-emerald-700",
    "صرف": "bg-purple-100 text-purple-700",
    "استعادة": "bg-orange-100 text-orange-700",
    "إزالة": "bg-gray-100 text-gray-700",
  };

  const getActionColor = (action) => {
    for (const [key, val] of Object.entries(actionColors)) {
      if (action.includes(key)) return val;
    }
    return "bg-gray-100 text-gray-700";
  };

  return (
    <div data-testid="audit-log-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-800">سجل العمليات</h3>
        <div className="flex gap-1">
          {[
            { id: "all", label: "الكل" },
            { id: "patient", label: "المرضى" },
            { id: "session", label: "الجلسات" },
            { id: "transaction", label: "المحاسبة" },
            { id: "employee", label: "الرواتب" },
            { id: "inventory", label: "المخزون" },
            { id: "system", label: "النظام" },
          ].map(f => (
            <button key={f.id} onClick={() => { setFilter(f.id); setLoading(true); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold ${filter === f.id ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-600"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      
      {loading ? <p className="text-center py-8 text-gray-400">جاري التحميل...</p> : logs.length === 0 ? (
        <p className="text-center py-8 text-gray-400">لا توجد عمليات مسجلة</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="border-b">
                  <th className="text-right py-2.5 px-4 text-xs font-bold text-gray-500">الوقت</th>
                  <th className="text-right py-2.5 px-4 text-xs font-bold text-gray-500">المستخدم</th>
                  <th className="text-right py-2.5 px-4 text-xs font-bold text-gray-500">العملية</th>
                  <th className="text-right py-2.5 px-4 text-xs font-bold text-gray-500">التفاصيل</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={`audit-${i}`} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2.5 px-4 text-xs text-gray-500 whitespace-nowrap">{fmtDate(log.timestamp)}</td>
                    <td className="py-2.5 px-4 text-xs font-medium">{log.user_name} <span className="text-gray-400">({log.user_role})</span></td>
                    <td className="py-2.5 px-4">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${getActionColor(log.action)}`}>{log.action}</span>
                    </td>
                    <td className="py-2.5 px-4 text-xs text-gray-600">
                      {log.entity_id && log.entity_type === "patient" ? (
                        <span className="cursor-pointer text-purple-600 hover:underline" onClick={() => navigate(`/patients/${log.entity_id}`)}>{log.details}</span>
                      ) : log.details}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};


const SettingsPage = () => {
  const { user, token } = useAuth();
  const [clinicSettings, setClinicSettings] = useState({
    clinicName: "المركز الوطني التركي لزراعة الشعر",
    phone: "",
    address: "",
    workingHours: "9:00 AM - 9:00 PM"
  });

  const [staffMembers, setStaffMembers] = useState({
    extraction: ["علي عدنان", "قاسم الافندي", "محمود الافندي"],
    channels: ["علي عدنان", "محمود الافندي"],
    planting: ["زارع 1", "زارع 2"],
    dataEntry: ["موظف 1", "موظف 2"]
  });

  // Salaries state
  const [salaries, setSalaries] = useState([]);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [salaryForm, setSalaryForm] = useState({ name: "", amount: "", position: "" });
  const [showPaySalaryModal, setShowPaySalaryModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState("myaccount"); // myaccount, clinic, staff, salaries, attendance, users, backup

  // Attendance state
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceForm, setAttendanceForm] = useState({ 
    employee_id: "", 
    date: new Date().toISOString().split('T')[0], 
    status: "absent", 
    reason: "",
    deduction_amount: 20000
  });
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  const [newMember, setNewMember] = useState({ category: "", name: "" });
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Categories management
  const [categories, setCategories] = useState({ income: [], expense: [] });
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryType, setNewCategoryType] = useState("income");
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState("");
  
  // Fetch categories
  const fetchCategories = async () => {
    try {
      const res = await api.get("/categories", token);
      setCategories(res.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };
  
  // Add new category
  const addCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("يرجى إدخال اسم الفئة");
      return;
    }
    try {
      await api.post("/categories", {
        name: newCategoryName.trim(),
        category_type: newCategoryType
      }, token);
      toast.success("تمت إضافة الفئة بنجاح");
      setNewCategoryName("");
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في إضافة الفئة");
    }
  };
  
  // Update category
  const updateCategory = async (categoryId) => {
    if (!editCategoryName.trim()) {
      toast.error("يرجى إدخال اسم الفئة");
      return;
    }
    try {
      await api.put(`/categories/${categoryId}`, {
        name: editCategoryName.trim()
      }, token);
      toast.success("تم تعديل الفئة بنجاح");
      setEditingCategory(null);
      setEditCategoryName("");
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في تعديل الفئة");
    }
  };
  
  // Delete category
  const deleteCategory = async (categoryId, isDefault) => {
    if (isDefault) {
      toast.error("لا يمكن حذف الفئات الأساسية");
      return;
    }
    if (!window.confirm("هل أنت متأكد من حذف هذه الفئة؟")) return;
    try {
      await api.delete(`/categories/${categoryId}`, token);
      toast.success("تم حذف الفئة بنجاح");
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في حذف الفئة");
    }
  };
  
  // Password management
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [newPasswordForUser, setNewPasswordForUser] = useState("");
  const [myPasswordForm, setMyPasswordForm] = useState({ current: "", new: "", confirm: "" });
  
  // Change my own password
  const changeMyPassword = async () => {
    if (!myPasswordForm.current || !myPasswordForm.new || !myPasswordForm.confirm) {
      toast.error("يرجى ملء جميع الحقول");
      return;
    }
    if (myPasswordForm.new !== myPasswordForm.confirm) {
      toast.error("كلمة المرور الجديدة غير متطابقة");
      return;
    }
    if (myPasswordForm.new.length < 3) {
      toast.error("كلمة المرور يجب أن تكون 3 أحرف على الأقل");
      return;
    }
    try {
      await api.post("/auth/change-password", {
        current_password: myPasswordForm.current,
        new_password: myPasswordForm.new
      }, token);
      toast.success("تم تغيير كلمة المرور بنجاح");
      setMyPasswordForm({ current: "", new: "", confirm: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في تغيير كلمة المرور");
    }
  };
  
  // Employee detail modal
  const [showEmployeeDetail, setShowEmployeeDetail] = useState(false);
  const [selectedEmployeeDetail, setSelectedEmployeeDetail] = useState(null);
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const [showQuickAttendance, setShowQuickAttendance] = useState(false);
  const [quickAttendanceDate, setQuickAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [quickAttendanceStatus, setQuickAttendanceStatus] = useState("absent");

  // Quick attendance from employee detail
  const addQuickAttendance = async () => {
    if (!selectedEmployeeDetail) return;
    try {
      const deduction = quickAttendanceStatus === "absent" ? 20000 : 
                       quickAttendanceStatus === "half_day" ? 10000 : 0;
      await api.post("/attendance", {
        employee_id: selectedEmployeeDetail.name,
        date: quickAttendanceDate,
        status: quickAttendanceStatus,
        deduction_amount: deduction
      }, token);
      toast.success("تم تسجيل الغياب بنجاح");
      setShowQuickAttendance(false);
      // Reload employee details
      viewEmployeeDetail(selectedEmployeeDetail);
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في تسجيل الغياب");
    }
  };

  // Export employee report to Excel
  const exportEmployeeToExcel = async (employee) => {
    if (!employee) return;
    toast.info("جاري التحضير...");
    const XLSX = await loadXLSX();
    
    const wb = XLSX.utils.book_new();
    
    // Employee info sheet
    const infoData = [
      { "البيان": "الاسم", "القيمة": employee.name },
      { "البيان": "المنصب", "القيمة": employee.position || "-" },
      { "البيان": "الراتب الشهري", "القيمة": employee.salary || 0 },
      { "البيان": "المدفوع", "القيمة": employee.paid || 0 },
      { "البيان": "إجمالي الخصومات", "القيمة": employee.attendance_summary?.total_deductions || 0 },
      { "البيان": "الصافي المتبقي", "القيمة": employee.net_remaining || 0 },
      { "البيان": "أيام الغياب", "القيمة": employee.attendance_summary?.absent_days || 0 },
      { "البيان": "أيام نصف يوم", "القيمة": employee.attendance_summary?.half_days || 0 },
      { "البيان": "الإجازات", "القيمة": employee.attendance_summary?.leave_days || 0 },
    ];
    const infoSheet = XLSX.utils.json_to_sheet(infoData);
    XLSX.utils.book_append_sheet(wb, infoSheet, "معلومات الموظف");
    
    // Attendance sheet
    if (employee.attendance?.length > 0) {
      const attendanceData = employee.attendance.map(r => ({
        "التاريخ": r.date,
        "الحالة": r.status === "absent" ? "غياب" : r.status === "half_day" ? "نصف يوم" : "إجازة",
        "السبب": r.reason || "-",
        "الخصم": r.deduction_amount || 0
      }));
      const attendanceSheet = XLSX.utils.json_to_sheet(attendanceData);
      XLSX.utils.book_append_sheet(wb, attendanceSheet, "سجل الحضور");
    }
    
    // Payments sheet
    if (employee.salary_payments?.length > 0) {
      const paymentsData = employee.salary_payments.map(p => ({
        "التاريخ": p.date,
        "المبلغ": p.amount || 0,
        "الوصف": p.description || "-"
      }));
      const paymentsSheet = XLSX.utils.json_to_sheet(paymentsData);
      XLSX.utils.book_append_sheet(wb, paymentsSheet, "سجل الدفعات");
    }
    
    XLSX.writeFile(wb, `تقرير_${employee.name}.xlsx`);
    toast.success("تم تصدير تقرير الموظف");
  };

  // Users management state
  const [users, setUsers] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userForm, setUserForm] = useState({ name: "", username: "", password: "", role: "staff" });

  // Load users
  const loadUsers = async () => {
    try {
      const res = await api.get("/users", token);
      setUsers(res.data);
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };

  // Add new user
  const addUser = async () => {
    if (!userForm.name || !userForm.username || !userForm.password) {
      toast.error("يرجى ملء جميع الحقول");
      return;
    }
    try {
      await api.post("/auth/register", userForm, token);
      toast.success("تم إضافة المستخدم بنجاح");
      setShowUserModal(false);
      setUserForm({ name: "", username: "", password: "", role: "staff" });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في إضافة المستخدم");
    }
  };

  // Delete user
  const deleteUser = async (userId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا المستخدم؟")) return;
    try {
      await api.delete(`/users/${userId}`, token);
      toast.success("تم حذف المستخدم");
      loadUsers();
    } catch (error) {
      toast.error("خطأ في حذف المستخدم");
    }
  };

  // Update user role
  const updateUserRole = async (userId, newRole) => {
    try {
      await api.put(`/users/${userId}`, { role: newRole }, token);
      toast.success("تم تحديث الصلاحية");
      loadUsers();
    } catch (error) {
      toast.error("خطأ في تحديث الصلاحية");
    }
  };

  const ROLE_PERMISSIONS_DISPLAY = {
    admin: { view: true, add: true, edit: true, delete: true, accounting: true, settings: true, website: true, ai: true, payroll: true, debts: true },
    accountant: { view: true, add: true, edit: false, delete: false, accounting: true, settings: false, website: false, ai: true, payroll: true, debts: true },
    staff: { view: true, add: true, edit: false, delete: false, accounting: false, settings: false, website: false, ai: false, payroll: false, debts: false },
    viewer: { view: true, add: false, edit: false, delete: false, accounting: false, settings: false, website: false, ai: false, payroll: false, debts: false }
  };

  const toggleUserPermission = async (user, permKey, currentValue) => {
    try {
      const existingCustom = user.custom_permissions || {};
      const updatedPerms = { ...existingCustom, [permKey]: !currentValue };
      await api.put(`/users/${user.id}/permissions`, { permissions: updatedPerms }, token);
      toast.success(`تم ${!currentValue ? "إضافة" : "إزالة"} الصلاحية`);
      loadUsers();
    } catch (error) {
      toast.error("خطأ في تحديث الصلاحية");
    }
  };

  // Load attendance records
  const loadAttendanceRecords = async () => {
    try {
      const res = await api.get(`/attendance?month=${selectedMonth}`, token);
      setAttendanceRecords(res.data);
    } catch (error) {
      console.error("Error loading attendance:", error);
    }
  };

  // Load settings from database (with localStorage fallback for migration)
  const loadSettings = async () => {
    try {
      // Load staff teams from database
      const settingsRes = await api.get("/clinic-settings", token);
      if (settingsRes.data?.staff) {
        setStaffMembers(settingsRes.data.staff);
      }
      
      // Load employees from database
      const employeesRes = await api.get("/employees", token);
      if (employeesRes.data?.length > 0) {
        setSalaries(employeesRes.data);
      } else {
        // Migration: check localStorage and migrate to database
        const savedSalaries = localStorage.getItem("clinic_salaries");
        if (savedSalaries) {
          const localSalaries = JSON.parse(savedSalaries);
          // Migrate each employee to database
          for (const emp of localSalaries) {
            try {
              await api.post("/employees", {
                name: emp.name,
                position: emp.position,
                salary: emp.amount || emp.salary || 0
              }, token);
            } catch (e) {
              console.log("Employee may already exist:", emp.name);
            }
          }
          // Reload from database
          const newRes = await api.get("/employees", token);
          setSalaries(newRes.data);
          localStorage.removeItem("clinic_salaries"); // Clear after migration
          toast.success("تم نقل بيانات الموظفين لقاعدة البيانات");
        }
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      // Fallback to localStorage
      const savedStaff = localStorage.getItem("clinic_staff");
      if (savedStaff) setStaffMembers(JSON.parse(savedStaff));
      const savedSalaries = localStorage.getItem("clinic_salaries");
      if (savedSalaries) setSalaries(JSON.parse(savedSalaries));
    }
  };

  useEffect(() => {
    if (token) {
      loadSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Load attendance when tab changes to attendance or month changes
  useEffect(() => {
    if (activeTab === "attendance" && token) {
      loadAttendanceRecords();
    }
    if (activeTab === "users" && token) {
      loadUsers();
    }
    if (activeTab === "categories" && token) {
      fetchCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedMonth, token]);

  // Backup functions
  const downloadBackup = async () => {
    try {
      toast.info("جاري إنشاء النسخة الاحتياطية...");
      const res = await api.get("/backup", token);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("تم تحميل النسخة الاحتياطية بنجاح");
    } catch (error) {
      toast.error("خطأ في إنشاء النسخة الاحتياطية");
    }
  };

  const downloadBackupExcel = async () => {
    try {
      toast.info("جاري إنشاء النسخة الاحتياطية...");
      const XLSX = await loadXLSX();
      const res = await api.get("/backup", token);
      const backup = res.data;
      
      const wb = XLSX.utils.book_new();
      
      // Patients sheet
      if (backup.patients?.length > 0) {
        const patientsData = backup.patients.map(p => ({
          "الاسم": p.name,
          "الهاتف": p.phone,
          "تاريخ العملية": p.operation_date,
          "المدفوع": p.total_paid || 0,
          "المتبقي": p.remaining_amount || 0,
          "نوع الزراعة": p.transplant_type,
          "عدد البصيلات": p.grafts_count
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(patientsData), "المرضى");
      }
      
      // Transactions sheet
      if (backup.transactions?.length > 0) {
        const transData = backup.transactions.map(t => ({
          "التاريخ": t.date,
          "النوع": t.transaction_type === "income" ? "إيراد" : "مصروف",
          "التصنيف": t.category,
          "المبلغ": t.amount,
          "الوصف": t.description
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(transData), "المعاملات");
      }
      
      XLSX.writeFile(wb, `backup_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success("تم تحميل النسخة الاحتياطية Excel");
    } catch (error) {
      toast.error("خطأ في إنشاء النسخة الاحتياطية");
    }
  };

  // Save staff settings to database
  const saveStaffSettings = async (newStaff) => {
    setStaffMembers(newStaff);
    try {
      await api.put("/clinic-settings/staff", newStaff, token);
      toast.success("تم حفظ فريق العمل");
    } catch (error) {
      // Fallback to localStorage
      localStorage.setItem("clinic_staff", JSON.stringify(newStaff));
      toast.success("تم حفظ الإعدادات محلياً");
    }
  };

  const addStaffMember = () => {
    if (!newMember.name.trim() || !newMember.category) {
      toast.error("يرجى إدخال الاسم واختيار القسم");
      return;
    }
    const updated = { ...staffMembers };
    if (!updated[newMember.category].includes(newMember.name)) {
      updated[newMember.category] = [...updated[newMember.category], newMember.name];
      saveStaffSettings(updated);
    }
    setNewMember({ category: "", name: "" });
    setShowAddModal(false);
  };

  const removeStaffMember = (category, name) => {
    const updated = { ...staffMembers };
    updated[category] = updated[category].filter(n => n !== name);
    saveStaffSettings(updated);
  };

  // Employee/Salary functions - using database
  const loadEmployees = async () => {
    try {
      const res = await api.get("/employees", token);
      setSalaries(res.data);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const addSalary = async () => {
    if (!salaryForm.name.trim() || !salaryForm.amount) {
      toast.error("يرجى إدخال الاسم والراتب");
      return;
    }
    try {
      await api.post("/employees", {
        name: salaryForm.name,
        position: salaryForm.position,
        salary: parseFloat(salaryForm.amount)
      }, token);
      await loadEmployees();
      setSalaryForm({ name: "", amount: "", position: "" });
      setShowSalaryModal(false);
      toast.success("تم إضافة الموظف بنجاح");
    } catch (error) {
      toast.error("خطأ في إضافة الموظف");
    }
  };

  const removeSalary = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الموظف؟")) return;
    try {
      await api.delete(`/employees/${id}`, token);
      await loadEmployees();
      toast.success("تم حذف الموظف");
    } catch (error) {
      toast.error("خطأ في حذف الموظف");
    }
  };

  // View employee detail
  const viewEmployeeDetail = async (employee) => {
    setLoadingEmployee(true);
    setShowEmployeeDetail(true);
    try {
      const res = await api.get(`/employees/${employee.id}`, token);
      setSelectedEmployeeDetail(res.data);
    } catch (error) {
      toast.error("خطأ في تحميل بيانات الموظف");
    }
    setLoadingEmployee(false);
  };

  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);

  const printSalaryReceipt = (employee, amount) => {
    const today = new Date().toLocaleDateString('en-GB');
    const receiptNo = Date.now().toString().slice(-8);
    
    const printContent = `
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8">
        <title>إيصال راتب</title>
        <style>
          body {
            font-family: 'Segoe UI', Tahoma, sans-serif;
            max-width: 350px;
            margin: 0 auto;
            padding: 20px;
            direction: rtl;
          }
          .header { text-align: center; border-bottom: 2px solid #10B981; padding-bottom: 15px; margin-bottom: 20px; }
          .header h1 { color: #1F2937; font-size: 16px; margin: 0; }
          .receipt-title { background: #10B981; color: white; text-align: center; padding: 10px; border-radius: 8px; margin-bottom: 20px; }
          .info-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dashed #E5E7EB; }
          .info-label { color: #6B7280; font-size: 13px; }
          .info-value { font-weight: bold; color: #1F2937; }
          .amount-box { background: #F3F4F6; border-radius: 10px; padding: 15px; margin: 20px 0; text-align: center; }
          .amount { font-size: 24px; font-weight: bold; color: #10B981; }
          .footer { text-align: center; margin-top: 30px; color: #9CA3AF; font-size: 11px; }
          .signatures { display: flex; justify-content: space-between; margin-top: 40px; }
          .sig-box { text-align: center; width: 45%; }
          .sig-line { border-top: 1px solid #6B7280; margin-bottom: 5px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>المركز الوطني التركي لزراعة الشعر</h1>
        </div>
        <div class="receipt-title">
          <h2 style="margin:0; font-size:14px;">إيصال دفع راتب</h2>
        </div>
        <div class="info-row">
          <span class="info-label">رقم الإيصال:</span>
          <span class="info-value">#${receiptNo}</span>
        </div>
        <div class="info-row">
          <span class="info-label">التاريخ:</span>
          <span class="info-value">${today}</span>
        </div>
        <div class="info-row">
          <span class="info-label">اسم الموظف:</span>
          <span class="info-value">${escapeHtml(employee.name)}</span>
        </div>
        <div class="info-row">
          <span class="info-label">المنصب:</span>
          <span class="info-value">${escapeHtml(employee.position || "-")}</span>
        </div>
        <div class="amount-box">
          <div style="color:#6B7280; font-size:12px;">المبلغ المدفوع</div>
          <div class="amount">${amount.toLocaleString()} د.ع</div>
        </div>
        <div class="signatures">
          <div class="sig-box"><div class="sig-line"></div><span>توقيع المستلم</span></div>
          <div class="sig-box"><div class="sig-line"></div><span>توقيع المسؤول</span></div>
        </div>
        <div class="footer"><p>هذا إيصال إلكتروني</p></div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    
    openPrintWindow(printContent);
  };

  const paySalary = async (shouldPrint = false) => {
    if (!selectedEmployee) return;
    
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      toast.error("يرجى إدخال مبلغ صحيح");
      return;
    }
    
    if (!paymentDate) {
      toast.error("يرجى اختيار التاريخ");
      return;
    }
    
    try {
      // Pay via API with date
      await api.post(`/employees/${selectedEmployee.id}/pay`, { amount, date: paymentDate }, token);
      await loadEmployees();
      toast.success(`تم دفع ${amount.toLocaleString()} لـ ${selectedEmployee.name}`);
      
      // Print receipt if requested
      if (shouldPrint) {
        printSalaryReceipt(selectedEmployee, amount);
      }
    } catch (error) {
      toast.error("خطأ في تسجيل الدفعة");
    }
    
    setShowPaySalaryModal(false);
    setSelectedEmployee(null);
    setPaymentAmount("");
  };

  const resetSalary = async (id) => {
    try {
      await api.post(`/employees/${id}/reset`, {}, token);
      await loadEmployees();
      toast.success("تم إعادة تعيين الراتب للشهر الجديد");
    } catch (error) {
      toast.error("خطأ في إعادة تعيين الراتب");
    }
  };

  const openPaySalaryModal = (employee) => {
    setSelectedEmployee(employee);
    setPaymentAmount("");
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setShowPaySalaryModal(true);
  };

  // Quick pay salary function
  const quickPaySalary = async (employee, amount) => {
    try {
      await api.post(`/employees/${employee.id}/pay`, { amount }, token);
      toast.success(`تم دفع ${amount.toLocaleString()} لـ ${employee.name}`);
      // Reload employees
      const res = await api.get("/employees", token);
      setSalaries(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في دفع الراتب");
    }
  };

  // Attendance functions
  const addAttendanceRecord = async () => {
    if (!attendanceForm.employee_id) {
      toast.error("يرجى اختيار الموظف");
      return;
    }
    if (!attendanceForm.date) {
      toast.error("يرجى اختيار التاريخ");
      return;
    }
    
    try {
      await api.post("/attendance", attendanceForm, token);
      toast.success("تم تسجيل الغياب بنجاح");
      setShowAttendanceModal(false);
      setAttendanceForm({ 
        employee_id: "", 
        date: new Date().toISOString().split('T')[0], 
        status: "absent", 
        reason: "",
        deduction_amount: 20000
      });
      loadAttendanceRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || "خطأ في تسجيل الغياب");
    }
  };

  const deleteAttendanceRecord = async (recordId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا السجل؟")) return;
    
    try {
      await api.delete(`/attendance/${recordId}`, token);
      toast.success("تم حذف السجل بنجاح");
      loadAttendanceRecords();
    } catch (error) {
      toast.error("خطأ في حذف السجل");
    }
  };

  // Calculate total deductions for an employee
  const getEmployeeDeductions = (employeeId) => {
    return attendanceRecords
      .filter(r => r.employee_id === employeeId)
      .reduce((sum, r) => sum + (r.deduction_amount || 0), 0);
  };

  // Get remaining salary after deductions
  const getEmployeeRemainingWithDeductions = (employee) => {
    const paid = employee.paid || 0;
    const deductions = getEmployeeDeductions(employee.name);
    return employee.amount - paid - deductions;
  };

  return (
    <div className="animate-fade-in" data-testid="settings-page">
      <div className="page-header">
        <h1 className="page-title">الإعدادات</h1>
        <p className="page-subtitle">إعدادات النظام والمركز</p>
      </div>

      {/* Tabs */}
      <div className="tabs mb-6 flex-wrap">
        <button className={`tab ${activeTab === "myaccount" ? "active" : ""}`} onClick={() => setActiveTab("myaccount")}>
          حسابي
        </button>
        <button className={`tab ${activeTab === "clinic" ? "active" : ""}`} onClick={() => setActiveTab("clinic")}>
          معلومات المركز
        </button>
        <button className={`tab ${activeTab === "categories" ? "active" : ""}`} onClick={() => setActiveTab("categories")}>
          فئات المحاسبة
        </button>
        <button className={`tab ${activeTab === "staff" ? "active" : ""}`} onClick={() => setActiveTab("staff")}>
          فريق العمل
        </button>
        <button className={`tab ${activeTab === "users" ? "active" : ""}`} onClick={() => setActiveTab("users")}>
          المستخدمين
        </button>
        <button className={`tab ${activeTab === "backup" ? "active" : ""}`} onClick={() => setActiveTab("backup")}>
          النسخ الاحتياطي
        </button>
        <button className={`tab ${activeTab === "reports" ? "active" : ""}`} onClick={() => setActiveTab("reports")}>
          التقارير
        </button>
        <button className={`tab ${activeTab === "staff-stats" ? "active" : ""}`} onClick={() => setActiveTab("staff-stats")}>
          إحصائيات الموظفين
        </button>
        <button className={`tab ${activeTab === "audit" ? "active" : ""}`} onClick={() => setActiveTab("audit")}>
          سجل العمليات
        </button>
      </div>

      {/* My Account Tab */}
      {activeTab === "myaccount" && (
        <div className="max-w-lg">
          <div className="card p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-500" />
              معلومات حسابي
            </h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="text-sm text-gray-500">الاسم</label>
                <p className="font-medium">{user?.name}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">البريد الإلكتروني</label>
                <p className="font-medium">{user?.email}</p>
              </div>
            </div>
            
            <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
              <KeyRound className="w-4 h-4" />
              تغيير كلمة المرور
            </h4>
            <div className="space-y-3">
              <div className="form-group">
                <label className="form-label">كلمة المرور الحالية *</label>
                <input
                  type="password"
                  className="form-input"
                  value={myPasswordForm.current}
                  onChange={(e) => setMyPasswordForm({...myPasswordForm, current: e.target.value})}
                  placeholder="أدخل كلمة المرور الحالية"
                />
              </div>
              <div className="form-group">
                <label className="form-label">كلمة المرور الجديدة *</label>
                <input
                  type="password"
                  className="form-input"
                  value={myPasswordForm.new}
                  onChange={(e) => setMyPasswordForm({...myPasswordForm, new: e.target.value})}
                  placeholder="أدخل كلمة المرور الجديدة"
                />
              </div>
              <div className="form-group">
                <label className="form-label">تأكيد كلمة المرور الجديدة *</label>
                <input
                  type="password"
                  className="form-input"
                  value={myPasswordForm.confirm}
                  onChange={(e) => setMyPasswordForm({...myPasswordForm, confirm: e.target.value})}
                  placeholder="أعد كتابة كلمة المرور الجديدة"
                />
              </div>
              <button
                onClick={changeMyPassword}
                className="btn btn-primary w-full"
              >
                تغيير كلمة المرور
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === "categories" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Income Categories */}
          <div className="data-table">
            <div className="table-header">
              <h3 className="table-title flex items-center gap-2">
                <ArrowUpCircle className="w-5 h-5 text-emerald-500" />
                فئات الإيرادات
              </h3>
            </div>
            <div className="p-4">
              <div className="space-y-2 mb-4">
                {categories.income.map((cat) => (
                  <div key={cat.id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    {editingCategory === cat.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="text"
                          className="form-input text-sm flex-1"
                          value={editCategoryName}
                          onChange={(e) => setEditCategoryName(e.target.value)}
                          autoFocus
                        />
                        <button
                          onClick={() => updateCategory(cat.id)}
                          className="p-1 text-emerald-600 hover:bg-emerald-100 rounded"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setEditingCategory(null); setEditCategoryName(""); }}
                          className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="text-gray-800 font-medium">{cat.name}</span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => { setEditingCategory(cat.id); setEditCategoryName(cat.name); }}
                            className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                            title="تعديل"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {!cat.is_default && (
                            <button
                              onClick={() => deleteCategory(cat.id, cat.is_default)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                              title="حذف"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Add New Income Category */}
              <div className="flex gap-2 pt-4 border-t">
                <input
                  type="text"
                  className="form-input text-sm flex-1"
                  placeholder="اسم فئة جديدة..."
                  value={newCategoryType === "income" ? newCategoryName : ""}
                  onChange={(e) => { setNewCategoryType("income"); setNewCategoryName(e.target.value); }}
                  onKeyPress={(e) => e.key === "Enter" && newCategoryType === "income" && addCategory()}
                />
                <button
                  onClick={() => { setNewCategoryType("income"); addCategory(); }}
                  className="btn btn-primary btn-sm"
                  disabled={newCategoryType !== "income" || !newCategoryName.trim()}
                >
                  <Plus className="w-4 h-4" />
                  إضافة
                </button>
              </div>
            </div>
          </div>

          {/* Expense Categories */}
          <div className="data-table">
            <div className="table-header">
              <h3 className="table-title flex items-center gap-2">
                <ArrowDownCircle className="w-5 h-5 text-red-500" />
                فئات المصروفات
              </h3>
            </div>
            <div className="p-4">
              <div className="space-y-2 mb-4">
                {categories.expense.map((cat) => (
                  <div key={cat.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                    {editingCategory === cat.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="text"
                          className="form-input text-sm flex-1"
                          value={editCategoryName}
                          onChange={(e) => setEditCategoryName(e.target.value)}
                          autoFocus
                        />
                        <button
                          onClick={() => updateCategory(cat.id)}
                          className="p-1 text-emerald-600 hover:bg-emerald-100 rounded"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setEditingCategory(null); setEditCategoryName(""); }}
                          className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="text-gray-800 font-medium">{cat.name}</span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => { setEditingCategory(cat.id); setEditCategoryName(cat.name); }}
                            className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                            title="تعديل"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {!cat.is_default && (
                            <button
                              onClick={() => deleteCategory(cat.id, cat.is_default)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                              title="حذف"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Add New Expense Category */}
              <div className="flex gap-2 pt-4 border-t">
                <input
                  type="text"
                  className="form-input text-sm flex-1"
                  placeholder="اسم فئة جديدة..."
                  value={newCategoryType === "expense" ? newCategoryName : ""}
                  onChange={(e) => { setNewCategoryType("expense"); setNewCategoryName(e.target.value); }}
                  onKeyPress={(e) => e.key === "Enter" && newCategoryType === "expense" && addCategory()}
                />
                <button
                  onClick={() => { setNewCategoryType("expense"); addCategory(); }}
                  className="btn btn-primary btn-sm"
                  disabled={newCategoryType !== "expense" || !newCategoryName.trim()}
                >
                  <Plus className="w-4 h-4" />
                  إضافة
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "clinic" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Clinic Info */}
          <div className="data-table">
            <div className="table-header">
              <h3 className="table-title flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-500" />
                معلومات المركز
              </h3>
            </div>
            <div className="p-4 space-y-4">
              <div className="form-group">
                <label className="form-label">اسم المركز</label>
                <input
                  type="text"
                  className="form-input"
                  value={clinicSettings.clinicName}
                  onChange={(e) => setClinicSettings({ ...clinicSettings, clinicName: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">رقم الهاتف</label>
                <input
                  type="tel"
                  className="form-input"
                  value={clinicSettings.phone}
                  onChange={(e) => setClinicSettings({ ...clinicSettings, phone: e.target.value })}
                  placeholder="+964..."
                />
              </div>
              <div className="form-group">
                <label className="form-label">العنوان</label>
                <input
                  type="text"
                  className="form-input"
                  value={clinicSettings.address}
                  onChange={(e) => setClinicSettings({ ...clinicSettings, address: e.target.value })}
                  placeholder="العراق - بغداد..."
                />
              </div>
              <div className="form-group">
                <label className="form-label">ساعات العمل</label>
                <input
                  type="text"
                  className="form-input"
                  value={clinicSettings.workingHours}
                  onChange={(e) => setClinicSettings({ ...clinicSettings, workingHours: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="data-table">
            <div className="table-header">
              <h3 className="table-title flex items-center gap-2">
                <User className="w-5 h-5 text-purple-500" />
                معلومات المستخدم
              </h3>
            </div>
            <div className="p-4">
              <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white text-2xl font-bold">
                  {user?.name?.charAt(0) || "U"}
                </div>
                <div>
                  <h4 className="font-bold text-lg">{user?.name || "مستخدم"}</h4>
                  <p className="text-gray-500">{user?.email}</p>
                  <span className="inline-block mt-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                    {user?.role === "admin" ? "مدير" : user?.role === "accountant" ? "محاسب" : "موظف"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "staff" && (
        <>
          {/* Staff Members */}
          <div className="data-table">
            <div className="table-header flex justify-between items-center">
              <h3 className="table-title flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-500" />
                فريق العمل
              </h3>
              <button 
                onClick={() => { setShowAddModal(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }} 
                className="btn btn-primary btn-sm"
                data-testid="add-staff-btn"
              >
                <Plus className="w-4 h-4" />
                إضافة عضو
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">فريق الاقتطاف</h4>
                <div className="flex flex-wrap gap-2">
                  {staffMembers.extraction.map((name, i) => (
                    <span key={i} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1">
                      {name}
                      <button onClick={() => removeStaffMember("extraction", name)} className="hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-2">فريق القنوات</h4>
                <div className="flex flex-wrap gap-2">
                  {staffMembers.channels.map((name, i) => (
                    <span key={i} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm flex items-center gap-1">
                      {name}
                      <button onClick={() => removeStaffMember("channels", name)} className="hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-2">فريق الزراعة</h4>
                <div className="flex flex-wrap gap-2">
                  {staffMembers.planting.map((name, i) => (
                    <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm flex items-center gap-1">
                      {name}
                      <button onClick={() => removeStaffMember("planting", name)} className="hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-2">مدخلي البيانات</h4>
                <div className="flex flex-wrap gap-2">
                  {staffMembers.dataEntry.map((name, i) => (
                    <span key={i} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm flex items-center gap-1">
                      {name}
                      <button onClick={() => removeStaffMember("dataEntry", name)} className="hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Add Staff Modal */}
          {showAddModal && (
            <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
              <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2 className="modal-title">إضافة عضو جديد</h2>
                  <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label className="form-label">القسم</label>
                    <select 
                      className="form-input" 
                      value={newMember.category}
                      onChange={(e) => setNewMember({ ...newMember, category: e.target.value })}
                    >
                      <option value="">اختر القسم</option>
                      <option value="extraction">فريق الاقتطاف</option>
                      <option value="channels">فريق القنوات</option>
                      <option value="planting">فريق الزراعة</option>
                      <option value="dataEntry">مدخلي البيانات</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">الاسم</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      value={newMember.name}
                      onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                      placeholder="أدخل اسم العضو"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button onClick={() => setShowAddModal(false)} className="btn btn-secondary">إلغاء</button>
                  <button onClick={addStaffMember} className="btn btn-primary">إضافة</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === "users" && (
        <div className="space-y-6">
          {/* Add User Button */}
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold text-gray-800">إدارة المستخدمين</h3>
            <button 
              onClick={() => setShowUserModal(true)}
              className="btn btn-primary"
              data-testid="add-user-btn"
            >
              <Plus className="w-4 h-4" />
              إضافة مستخدم
            </button>
          </div>

          {/* Users Table */}
          <div className="data-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-right py-3 px-4 font-medium text-gray-500">الاسم</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">اسم المستخدم</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">كلمة المرور</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">الصلاحية</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-gray-500">لا يوجد مستخدمين</td>
                    </tr>
                  ) : (
                    users.map((u) => (
                      <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">{u.name}</td>
                        <td className="py-3 px-4 text-gray-600">{u.username || u.email}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-sm">••••••</span>
                            <button 
                              onClick={() => {
                                setSelectedUserForPassword(u);
                                setShowPasswordModal(true);
                              }}
                              className="btn btn-sm bg-blue-100 text-blue-700 hover:bg-blue-200"
                              title="تغيير كلمة المرور"
                            >
                              <KeyRound className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <select
                            value={u.role || "staff"}
                            onChange={(e) => updateUserRole(u.id, e.target.value)}
                            className="form-input py-1 px-2 text-sm w-24"
                          >
                            <option value="admin">مدير</option>
                            <option value="accountant">محاسب</option>
                            <option value="staff">موظف</option>
                            <option value="viewer">مشاهد</option>
                          </select>
                        </td>
                        <td className="py-3 px-4">
                          <button 
                            onClick={() => deleteUser(u.id)}
                            className="btn btn-sm bg-red-100 text-red-700 hover:bg-red-200"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Change Password Modal */}

          {/* Permissions Matrix - Editable */}
          <div className="card p-5">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-purple-600" />
              جدول الصلاحيات (اضغط لتغيير)
            </h3>
            {users.filter(u => u.role !== "admin").length === 0 ? (
              <p className="text-gray-500 text-center py-4">لا يوجد مستخدمين غير المدير</p>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="py-2 px-3 text-right font-bold text-gray-600">الصلاحية</th>
                    {users.filter(u => u.role !== "admin").map(u => (
                      <th key={u.id} className="py-2 px-3 text-center font-bold text-gray-700 min-w-[80px]">
                        <div>{u.name}</div>
                        <div className="text-[10px] font-normal text-gray-400">{{"accountant": "محاسب", "staff": "موظف", "viewer": "مشاهد"}[u.role] || u.role}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { key: "view", name: "عرض المرضى" },
                    { key: "add", name: "إضافة مريض/جلسة" },
                    { key: "edit", name: "تعديل البيانات" },
                    { key: "delete", name: "حذف" },
                    { key: "accounting", name: "المحاسبة" },
                    { key: "payroll", name: "الرواتب" },
                    { key: "debts", name: "الديون" },
                    { key: "ai", name: "أدوات AI" },
                    { key: "settings", name: "الإعدادات" },
                    { key: "website", name: "إدارة الموقع" },
                  ].map(perm => (
                    <tr key={perm.key} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-3 font-medium text-gray-700">{perm.name}</td>
                      {users.filter(u => u.role !== "admin").map(u => {
                        const rolePerms = ROLE_PERMISSIONS_DISPLAY[u.role] || ROLE_PERMISSIONS_DISPLAY["viewer"];
                        const customPerms = u.custom_permissions || {};
                        const hasCustom = perm.key in customPerms;
                        const isEnabled = hasCustom ? customPerms[perm.key] : (rolePerms[perm.key] || false);
                        return (
                          <td key={u.id} className="py-2 px-3 text-center">
                            <button 
                              onClick={() => toggleUserPermission(u, perm.key, isEnabled)}
                              data-testid={`perm-toggle-${u.username || u.id}-${perm.key}`}
                              data-enabled={isEnabled ? "true" : "false"}
                              className={`w-7 h-7 rounded-lg flex items-center justify-center mx-auto transition-all ${isEnabled ? "bg-emerald-100 hover:bg-emerald-200" : "bg-red-50 hover:bg-red-100"} ${hasCustom ? "ring-2 ring-blue-300" : ""}`}
                              title={hasCustom ? "صلاحية مخصصة (اضغط لتغيير)" : "صلاحية افتراضية (اضغط لتخصيص)"}
                            >
                              {isEnabled ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <X className="w-4 h-4 text-red-400" />}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
              <span className="flex items-center gap-1"><span className="w-4 h-4 rounded ring-2 ring-blue-300 inline-block"></span> صلاحية مخصصة</span>
              <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-gray-100 inline-block"></span> صلاحية افتراضية حسب الدور</span>
            </div>
          </div>

          {/* Change Password Modal */}
          {showPasswordModal && selectedUserForPassword && (
            <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
              <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2 className="modal-title">تغيير كلمة المرور</h2>
                  <button onClick={() => setShowPasswordModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="modal-body">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-blue-700">
                      <strong>المستخدم:</strong> {selectedUserForPassword.name}
                    </p>
                    <p className="text-sm text-blue-600">
                      <strong>المستخدم:</strong> {selectedUserForPassword.username || selectedUserForPassword.email}
                    </p>
                  </div>
                  <div className="form-group">
                    <label className="form-label">كلمة المرور الجديدة *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={newPasswordForUser}
                      onChange={(e) => setNewPasswordForUser(e.target.value)}
                      placeholder="أدخل كلمة المرور الجديدة"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    onClick={async () => {
                      if (!newPasswordForUser) {
                        toast.error("أدخل كلمة المرور الجديدة");
                        return;
                      }
                      try {
                        await api.put(`/users/${selectedUserForPassword.id}/password`, { new_password: newPasswordForUser }, token);
                        toast.success("تم تغيير كلمة المرور بنجاح");
                        setShowPasswordModal(false);
                        setNewPasswordForUser("");
                        setSelectedUserForPassword(null);
                      } catch (error) {
                        toast.error("خطأ في تغيير كلمة المرور");
                      }
                    }}
                    className="btn btn-primary"
                  >
                    حفظ
                  </button>
                  <button onClick={() => setShowPasswordModal(false)} className="btn btn-secondary">
                    إلغاء
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add User Modal */}
          {showUserModal && (
            <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
              <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2 className="modal-title">إضافة مستخدم جديد</h2>
                  <button onClick={() => setShowUserModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label className="form-label">الاسم</label>
                    <input
                      type="text"
                      className="form-input"
                      value={userForm.name}
                      onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                      placeholder="اسم المستخدم الكامل"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">اسم المستخدم (للدخول)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={userForm.username}
                      onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                      placeholder="مثال: ahmed123"
                      dir="ltr"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">كلمة السر</label>
                    <input
                      type="password"
                      className="form-input"
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      placeholder="••••••••"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">الصلاحية</label>
                    <select
                      className="form-input"
                      value={userForm.role}
                      onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                    >
                      <option value="admin">مدير</option>
                      <option value="accountant">محاسب</option>
                      <option value="staff">موظف</option>
                      <option value="viewer">مشاهد</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button onClick={() => setShowUserModal(false)} className="btn btn-secondary">إلغاء</button>
                  <button onClick={addUser} className="btn btn-primary">
                    <Plus className="w-4 h-4" />
                    إضافة
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "backup" && (
        <BackupSection token={token} staffMembers={staffMembers} salaries={salaries} />
      )}

      {/* Reports Tab */}
      {activeTab === "reports" && <ReportsPage />}

      {/* Staff Stats Tab */}
      {activeTab === "staff-stats" && <StaffStatsPage />}

      {/* Audit Log Tab */}
      {activeTab === "audit" && <AuditLogSection token={token} />}
    </div>
  );
};

// Backup Section Component
const BackupSection = ({ token }) => {
  const [backupHistory, setBackupHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await api.get("/backup/history", token);
        setBackupHistory(res.data);
      } catch (err) { console.error(err); }
    };
    fetchHistory();
  }, [token]);

  const downloadBackupNow = async () => {
    setLoading(true);
    try {
      toast.info("جاري إنشاء النسخة الاحتياطية...");
      const response = await fetch(`${API}/backup/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup_${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("تم تحميل النسخة الاحتياطية");
      // Refresh history
      const res = await api.get("/backup/history", token);
      setBackupHistory(res.data);
    } catch (err) { toast.error("خطأ في التحميل"); }
    finally { setLoading(false); }
  };

  const handleRestore = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!window.confirm("تحذير: استعادة النسخة الاحتياطية ستستبدل جميع البيانات الحالية!\n\nهل أنت متأكد؟")) {
      e.target.value = "";
      return;
    }
    setRestoreLoading(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await api.post("/backup/restore", data, token);
      toast.success("تم استعادة النسخة الاحتياطية بنجاح!");
      window.location.reload();
    } catch (err) {
      toast.error("خطأ في استعادة النسخة - تأكد من صحة الملف");
    } finally {
      setRestoreLoading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-6">
      {/* Auto Backup Status */}
      <div className="card p-4 bg-gradient-to-r from-blue-500 to-emerald-500 text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Database className="w-8 h-8" />
            <div>
              <h3 className="font-bold text-lg">النسخ الاحتياطي</h3>
              <p className="text-blue-100 text-sm">تلقائي كل يوم الساعة 3 صباحاً + يدوي</p>
            </div>
          </div>
          <button 
            onClick={downloadBackupNow}
            disabled={loading}
            className="btn bg-white text-blue-600 hover:bg-blue-50"
          >
            <Download className="w-4 h-4" />
            {loading ? "جاري..." : "تحميل نسخة الآن"}
          </button>
        </div>
      </div>

      {/* Download Options */}
      <div className="card p-5">
        <h3 className="font-bold text-gray-800 mb-4">تحميل النسخة الاحتياطية</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button 
            onClick={downloadBackupNow}
            disabled={loading}
            className="p-4 border-2 border-dashed border-blue-300 rounded-xl hover:bg-blue-50 transition-all flex flex-col items-center gap-2"
          >
            <Download className="w-8 h-8 text-blue-600" />
            <span className="font-medium">تحميل JSON</span>
            <span className="text-sm text-gray-500">ملف كامل للاستعادة</span>
          </button>
          
          <label className="p-4 border-2 border-dashed border-orange-300 rounded-xl hover:bg-orange-50 transition-all flex flex-col items-center gap-2 cursor-pointer">
            <Archive className="w-8 h-8 text-orange-600" />
            <span className="font-medium">{restoreLoading ? "جاري الاستعادة..." : "استعادة نسخة احتياطية"}</span>
            <span className="text-sm text-gray-500">رفع ملف JSON محفوظ</span>
            <input type="file" accept=".json" onChange={handleRestore} className="hidden" disabled={restoreLoading} />
          </label>
        </div>
      </div>

      {/* Backup History */}
      <div className="card p-5">
        <h3 className="font-bold text-gray-800 mb-4">سجل النسخ الاحتياطية</h3>
        {backupHistory.length === 0 ? (
          <p className="text-gray-500 text-center py-4">لا توجد نسخ احتياطية سابقة</p>
        ) : (
          <div className="space-y-2">
            {backupHistory.map((b, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${b.type === "auto_daily" ? "bg-blue-500" : "bg-emerald-500"}`}>
                    {b.type === "auto_daily" ? "A" : "M"}
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800">
                      {b.type === "auto_daily" ? "تلقائي يومي" : "يدوي"} - {b.records || 0} سجل
                    </p>
                    <p className="text-xs text-gray-500">{b.date?.split("T")[0]} • {b.date?.split("T")[1]?.split(".")[0]} • {b.created_by}</p>
                  </div>
                </div>
                <CheckCircle className={`w-5 h-5 ${b.type === "auto_daily" ? "text-blue-400" : "text-emerald-400"}`} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="card p-5 bg-amber-50 border-amber-200">
        <div className="flex items-start gap-3">
          <Bell className="w-6 h-6 text-amber-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-800">معلومات النسخ الاحتياطي</h4>
            <ul className="text-amber-700 text-sm mt-1 space-y-1">
              <li>• النسخ التلقائي يعمل كل يوم الساعة 3 صباحاً</li>
              <li>• يتم حفظ آخر 7 نسخ تلقائياً</li>
              <li>• النسخة تشمل: المرضى، الجلسات، المعاملات، الرواتب، الموظفين</li>
              <li>• احتفظ بالنسخ اليدوية في مكان آمن (Google Drive, USB)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Accounting Page
const AccountingPage = () => {
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeMainTab, setActiveMainTab] = useState("transactions");
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [filter, setFilter] = useState("all"); // all, income, expense
  const [patients, setPatients] = useState([]);
  const [cashBalance, setCashBalance] = useState(null);
  const [pendingInstallments, setPendingInstallments] = useState(0);
  const [pendingInstallmentsList, setPendingInstallmentsList] = useState([]);
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  
  // Date filter states
  const [dateFilter, setDateFilter] = useState("today"); // today, range, all
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [sortBy, setSortBy] = useState("date"); // date, created_at
  
  const [formData, setFormData] = useState({
    transaction_type: "income",
    category: "",
    custom_category: "",
    amount: "",
    description: "",
    details: "",
    patient_id: "",
    payment_method: "نقدي",
    date: new Date().toISOString().split('T')[0]
  });

  const fetchData = async () => {
    try {
      const [transactionsRes, summaryRes, patientsRes, balanceRes, categoriesRes] = await Promise.all([
        api.get("/transactions", token),
        api.get("/financial-summary", token),
        api.get("/patients", token),
        api.get("/cash-balance", token),
        api.get("/categories", token)
      ]);
      setTransactions(transactionsRes.data);
      setSummary(summaryRes.data);
      setPatients(patientsRes.data);
      setCashBalance(balanceRes.data);
      
      // Calculate pending installments - المبلغ اللي ينزل = السعر - خصم الشركة
      const instPatients = (patientsRes.data || []).filter(p => p.payment_type === "installment" && p.installment_amount > 0 && p.installment_status !== "received");
      setPendingInstallments(instPatients.reduce((s, p) => s + ((p.price || 0) - (p.installment_amount || 0)), 0));
      setPendingInstallmentsList(instPatients);
      
      // Set categories from API
      const incomeCats = categoriesRes.data.income.map(c => ({ value: c.name, label: c.name }));
      const expenseCats = categoriesRes.data.expense.map(c => ({ value: c.name, label: c.name }));
      setIncomeCategories(incomeCats);
      setExpenseCategories(expenseCats);
      
      // Set default category
      if (incomeCats.length > 0) {
        setFormData(prev => ({ ...prev, category: incomeCats[0].value }));
      }
    } catch (error) {
      toast.error("خطأ في تحميل البيانات المالية");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const confirmInstallmentReceived = async (patient) => {
    if (!window.confirm(`تأكيد استلام مبلغ ${((patient.price || 0) - (patient.installment_amount || 0)).toLocaleString()} من ${patient.installment_company || "الشركة"} للمريض ${patient.name}؟`)) return;
    try {
      await api.put(`/patients/${patient.id}`, {
        installment_status: "received",
        installment_received_date: new Date().toISOString().split('T')[0]
      }, token);
      toast.success("تم تأكيد استلام القسط - المبلغ نزل بالرصيد");
      fetchData();
    } catch (err) { toast.error("خطأ في التحديث"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const finalCategory = formData.category === "أخرى" && formData.custom_category
        ? formData.custom_category
        : formData.category;
      
      const transactionData = {
        ...formData,
        category: finalCategory,
        amount: parseFloat(formData.amount),
        patient_id: formData.patient_id || null
      };
      delete transactionData.custom_category;
      
      await api.post("/transactions", transactionData, token);
      toast.success("تم إضافة المعاملة بنجاح");
      setShowModal(false);
      setFormData({
        transaction_type: "income",
        category: "عملية",
        amount: "",
        description: "",
        details: "",
        patient_id: "",
        payment_method: "نقدي",
        date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      toast.error("خطأ في إضافة المعاملة");
    }
  };

  const deleteTransaction = async (id) => {
    if (window.confirm("هل أنت متأكد من حذف هذه المعاملة؟")) {
      try {
        await api.delete(`/transactions/${id}`, token);
        toast.success("تم حذف المعاملة");
        fetchData();
      } catch (error) {
        toast.error("خطأ في حذف المعاملة");
      }
    }
  };

  // Edit transaction
  const openEditModal = (transaction) => {
    setEditingTransaction(transaction);
    setFormData({
      transaction_type: transaction.transaction_type,
      category: transaction.category,
      custom_category: transaction.custom_category || "",
      amount: transaction.amount?.toString() || "",
      description: transaction.description || "",
      details: transaction.details || "",
      patient_id: transaction.patient_id || "",
      payment_method: transaction.payment_method || "نقدي",
      date: transaction.date || new Date().toISOString().split('T')[0]
    });
    setShowModal(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateTransaction = async () => {
    if (!editingTransaction) return;
    try {
      await api.put(`/transactions/${editingTransaction.id}`, {
        ...formData,
        amount: parseFloat(formData.amount)
      }, token);
      toast.success("تم تعديل المعاملة بنجاح");
      setShowModal(false);
      setEditingTransaction(null);
      setFormData({
        transaction_type: "income",
        category: "عملية",
        custom_category: "",
        amount: "",
        description: "",
        details: "",
        patient_id: "",
        payment_method: "نقدي",
        date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      toast.error("خطأ في تعديل المعاملة");
    }
  };

  const filteredTransactions = transactions.filter(t => {
    // Filter by type
    if (filter !== "all" && t.transaction_type !== filter) return false;
    
    // Filter by date
    if (dateFilter === "today") {
      const today = new Date().toISOString().split('T')[0];
      return t.date === today;
    } else if (dateFilter === "month") {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      return t.date >= monthStart && t.date <= monthEnd;
    } else if (dateFilter === "range") {
      return t.date >= startDate && t.date <= endDate;
    }
    return true; // "all" - no date filter
  }).sort((a, b) => {
    if (sortBy === "created_at") {
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    }
    // Default: sort by transaction date desc, then by created_at desc
    if (a.date !== b.date) return b.date.localeCompare(a.date);
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });
  
  // Calculate filtered summary
  const filteredSummary = {
    income: filteredTransactions.filter(t => t.transaction_type === "income").reduce((sum, t) => sum + (t.amount || 0), 0),
    expense: filteredTransactions.filter(t => t.transaction_type === "expense").reduce((sum, t) => sum + (t.amount || 0), 0),
  };
  filteredSummary.net = filteredSummary.income - filteredSummary.expense;

  const categoryLabels = {
    "عملية": "عملية زراعة",
    "جلسة_خارجية": "جلسة خارجية",
    "بلازما_خارجي": "بلازما خارجي",
    "شامبو": "بيع شامبو",
    "نظارة": "نظارة",
    "رواتب": "رواتب",
    "إيجار": "إيجار",
    "مواد": "مواد",
    "صيانة": "صيانة",
    "أخرى": "أخرى"
  };

  // Export transactions to Excel
  const exportTransactionsToExcel = async (type = "all") => {
    toast.info("جاري التحضير...");
    const XLSX = await loadXLSX();
    let dataToExport = transactions;
    let fileName = "جميع_المعاملات";
    
    const today = new Date().toISOString().split('T')[0];
    const thisMonth = today.substring(0, 7);
    
    if (type === "daily") {
      dataToExport = transactions.filter(t => t.date === today);
      fileName = `تقرير_يومي_${today}`;
    } else if (type === "monthly") {
      dataToExport = transactions.filter(t => t.date?.startsWith(thisMonth));
      fileName = `تقرير_شهري_${thisMonth}`;
    }
    
    const exportData = dataToExport.map(t => ({
      "التاريخ": t.date || "",
      "النوع": t.transaction_type === "income" ? "إيراد" : "مصروف",
      "التصنيف": t.category || "",
      "الوصف": t.description || "",
      "المبلغ": t.amount || 0,
      "طريقة الدفع": t.payment_method || ""
    }));
    
    // Add summary row
    const totalIncome = dataToExport.filter(t => t.transaction_type === "income").reduce((s, t) => s + (t.amount || 0), 0);
    const totalExpense = dataToExport.filter(t => t.transaction_type === "expense").reduce((s, t) => s + (t.amount || 0), 0);
    
    exportData.push({});
    exportData.push({ "التاريخ": "الإجمالي", "النوع": "", "التصنيف": "", "الوصف": "", "المبلغ": "", "طريقة الدفع": "" });
    exportData.push({ "التاريخ": "إجمالي الإيرادات", "النوع": "", "التصنيف": "", "الوصف": "", "المبلغ": totalIncome, "طريقة الدفع": "" });
    exportData.push({ "التاريخ": "إجمالي المصروفات", "النوع": "", "التصنيف": "", "الوصف": "", "المبلغ": totalExpense, "طريقة الدفع": "" });
    exportData.push({ "التاريخ": "صافي الربح", "النوع": "", "التصنيف": "", "الوصف": "", "المبلغ": totalIncome - totalExpense, "طريقة الدفع": "" });
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "المعاملات");
    XLSX.writeFile(wb, `${fileName}.xlsx`);
    toast.success(`تم تصدير ${dataToExport.length} معاملة`);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse-soft text-gray-500">جاري التحميل...</div></div>;
  }

  return (
    <div className="animate-fade-in" data-testid="accounting-page">
      <div className="page-header flex justify-between items-start">
        <div>
          <h1 className="page-title">المحاسبة</h1>
          <p className="page-subtitle">إدارة الإيرادات والمصروفات والتقارير المالية</p>
        </div>
        {activeMainTab === "transactions" && (
        <div className="flex gap-2">
          <div className="relative group">
            <button className="btn btn-secondary flex items-center gap-2">
              <Download className="w-5 h-5" />
              تصدير Excel
            </button>
            <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-lg border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[150px]">
              <button onClick={() => exportTransactionsToExcel("daily")} className="w-full text-right px-4 py-2 hover:bg-gray-50 text-sm">
                تقرير اليوم
              </button>
              <button onClick={() => exportTransactionsToExcel("monthly")} className="w-full text-right px-4 py-2 hover:bg-gray-50 text-sm">
                تقرير الشهر
              </button>
              <button onClick={() => exportTransactionsToExcel("all")} className="w-full text-right px-4 py-2 hover:bg-gray-50 text-sm border-t">
                جميع المعاملات
              </button>
            </div>
          </div>
          <button onClick={() => { setShowModal(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="btn btn-primary" data-testid="add-transaction-btn">
            <Plus className="w-5 h-5" />
            إضافة معاملة
          </button>
        </div>
        )}
      </div>

      {/* Main Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {[
          { id: "transactions", label: "المعاملات", icon: FileText },
          { id: "advanced", label: "لوحة مالية وتقارير", icon: BarChart3 },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveMainTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeMainTab === tab.id ? "bg-emerald-600 text-white shadow-lg" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            data-testid={`acc-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {activeMainTab === "advanced" && <AdvancedAccountingPage />}

      {activeMainTab === "transactions" && (<>

      {/* Financial Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="stat-card card-hover">
          <div className="stat-icon bg-emerald-100 text-emerald-600">
            <ArrowUpCircle className="w-6 h-6" />
          </div>
          <div className="stat-value text-emerald-600">{formatNumber(summary?.today?.income)}</div>
          <div className="stat-label">إيرادات اليوم</div>
        </div>
        <div className="stat-card card-hover">
          <div className="stat-icon bg-red-100 text-red-600">
            <ArrowDownCircle className="w-6 h-6" />
          </div>
          <div className="stat-value text-red-600">{formatNumber(summary?.today?.expenses)}</div>
          <div className="stat-label">مصروفات اليوم</div>
        </div>
        <div className="stat-card card-hover bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="stat-icon bg-blue-200 text-blue-700">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div className="stat-value" style={{ color: ((summary?.today?.income || 0) - (summary?.today?.expenses || 0)) >= 0 ? '#059669' : '#dc2626' }}>
            {formatNumber((summary?.today?.income || 0) - (summary?.today?.expenses || 0))}
          </div>
          <div className="stat-label font-bold text-blue-700">صافي اليوم</div>
        </div>
        <div className="stat-card card-hover">
          <div className="stat-icon bg-purple-100 text-purple-600">
            <Wallet className="w-6 h-6" />
          </div>
          <div className="stat-value text-purple-600">{formatNumber(summary?.month?.income)}</div>
          <div className="stat-label">إيرادات الشهر</div>
        </div>
        <div className="stat-card card-hover">
          <div className="stat-icon bg-amber-100 text-amber-600">
            <DollarSign className="w-6 h-6" />
          </div>
          <div className="stat-value" style={{ color: (cashBalance?.total_balance || 0) >= 0 ? '#7c3aed' : '#dc2626' }}>
            {formatNumber(cashBalance?.total_balance)}
          </div>
          <div className="stat-label">الرصيد الإجمالي</div>
        </div>
        {pendingInstallments > 0 && (
        <div className="stat-card card-hover border-2 border-amber-300 bg-amber-50">
          <div className="stat-icon bg-amber-200 text-amber-700">
            <CreditCard className="w-6 h-6" />
          </div>
          <div className="stat-value text-amber-700">
            {formatNumber(pendingInstallments)}
          </div>
          <div className="stat-label text-amber-600">أقساط معلقة ({pendingInstallmentsList.length})</div>
        </div>
        )}
      </div>

      {/* Pending Installments Section */}
      {pendingInstallmentsList.length > 0 && (
      <div className="card p-4 mb-4 border-2 border-amber-200 bg-gradient-to-l from-amber-50 to-white">
        <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          أقساط بانتظار التحويل ({pendingInstallmentsList.length})
        </h3>
        <div className="space-y-2">
          {pendingInstallmentsList.map(p => (
            <div key={p.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-amber-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold">
                  {(p.name || "?").charAt(0)}
                </div>
                <div>
                  <p className="font-bold text-sm text-gray-800">{p.name} {p.last_name || ""}</p>
                  <p className="text-xs text-gray-500">
                    {p.installment_company && <span>{p.installment_company} • </span>}
                    ينزل: <strong className="text-emerald-600">{((p.price || 0) - (p.installment_amount || 0)).toLocaleString()}</strong>
                    {p.installment_expected_date && <span> • متوقع: {p.installment_expected_date}</span>}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => confirmInstallmentReceived(p)}
                className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-bold hover:bg-emerald-600 transition-colors flex items-center gap-1"
              >
                <CheckCircle className="w-3.5 h-3.5" /> تم الاستلام
              </button>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Filter Tabs */}
      <div className="tabs mb-4">
        <button className={`tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
          الكل ({transactions.length})
        </button>
        <button className={`tab ${filter === 'income' ? 'active' : ''}`} onClick={() => setFilter('income')}>
          الإيرادات ({transactions.filter(t => t.transaction_type === 'income').length})
        </button>
        <button className={`tab ${filter === 'expense' ? 'active' : ''}`} onClick={() => setFilter('expense')}>
          المصروفات ({transactions.filter(t => t.transaction_type === 'expense').length})
        </button>
      </div>

      {/* Date Filter */}
      <div className="card p-4 mb-4">
        <div className="flex flex-wrap items-center gap-4">
          <span className="font-medium text-gray-700">فلترة بالتاريخ:</span>
          <div className="flex gap-2 flex-wrap">
            <button 
              className={`btn btn-sm ${dateFilter === 'today' ? 'btn-primary' : 'bg-gray-100 hover:bg-gray-200'}`}
              onClick={() => setDateFilter('today')}
            >
              اليوم
            </button>
            <button 
              className={`btn btn-sm ${dateFilter === 'month' ? 'btn-primary' : 'bg-gray-100 hover:bg-gray-200'}`}
              onClick={() => setDateFilter('month')}
            >
              هذا الشهر
            </button>
            <button 
              className={`btn btn-sm ${dateFilter === 'range' ? 'btn-primary' : 'bg-gray-100 hover:bg-gray-200'}`}
              onClick={() => setDateFilter('range')}
            >
              فترة محددة
            </button>
            <button 
              className={`btn btn-sm ${dateFilter === 'all' ? 'btn-primary' : 'bg-gray-100 hover:bg-gray-200'}`}
              onClick={() => setDateFilter('all')}
            >
              الكل
            </button>
          </div>
          
          <div className="flex items-center gap-2 mr-auto">
            <span className="text-sm text-gray-500">ترتيب:</span>
            <select 
              className="form-input text-sm py-1 px-2 w-auto"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="sort-transactions-select"
            >
              <option value="date">تاريخ المعاملة</option>
              <option value="created_at">تاريخ الإضافة</option>
            </select>
          </div>

          {dateFilter === 'range' && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-gray-500">من:</span>
              <input 
                type="date" 
                className="form-input text-sm py-1 px-2 w-auto"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span className="text-gray-500">إلى:</span>
              <input 
                type="date" 
                className="form-input text-sm py-1 px-2 w-auto"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          )}
        </div>
        
        {/* Filtered Summary */}
        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
          <div className="text-center">
            <div className="text-sm text-gray-500">الإيرادات</div>
            <div className="text-lg font-bold text-emerald-600">{formatNumber(filteredSummary.income)}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-gray-500">المصروفات</div>
            <div className="text-lg font-bold text-red-600">{formatNumber(filteredSummary.expense)}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-gray-500">الصافي</div>
            <div className={`text-lg font-bold ${filteredSummary.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {formatNumber(filteredSummary.net)}
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="data-table">
        <div className="table-header">
          <h3 className="table-title">المعاملات المالية ({filteredTransactions.length})</h3>
        </div>
        {filteredTransactions.length === 0 ? (
          <div className="empty-state py-8">
            <Wallet className="empty-state-icon" />
            <h3>لا توجد معاملات</h3>
            <p>لا توجد معاملات في هذه الفترة</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-right py-3 px-4 font-medium text-gray-500">التاريخ</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">النوع</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">الفئة</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">المريض</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">الوصف</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">الدفع</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">المبلغ</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500"></th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((t) => (
                  <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`transaction-${t.id}`}>
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-medium">{t.date}</div>
                        <div className="text-[10px] text-gray-400 mt-0.5">أضيفت: {t.created_at ? formatDateTime(t.created_at) : ""}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        t.transaction_type === "income" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                      }`}>
                        {t.transaction_type === "income" ? "إيراد" : "مصروف"}
                      </span>
                    </td>
                    <td className="py-3 px-4">{categoryLabels[t.category] || t.category}</td>
                    <td className="py-3 px-4">
                      {t.patient_id ? (
                        <span className="text-purple-600 hover:text-purple-800 cursor-pointer font-medium hover:underline" onClick={() => navigate(`/patients/${t.patient_id}`)}>{t.patient_name || "-"}</span>
                      ) : (t.patient_name || "-")}
                    </td>
                    <td className="py-3 px-4 text-gray-500">
                      <div>
                        {t.description || "-"}
                        {t.details && <div className="text-[10px] text-blue-500 mt-0.5">{t.details}</div>}
                      </div>
                    </td>
                    <td className="py-3 px-4">{t.payment_method || "-"}</td>
                    <td className={`py-3 px-4 font-bold ${
                      t.transaction_type === "income" ? "text-emerald-600" : "text-red-600"
                    }`}>
                      {t.transaction_type === "income" ? "+" : "-"}{formatNumber(t.amount)}
                    </td>
                    <td className="py-3 px-4">
                      {isAdmin() && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => openEditModal(t)}
                          className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg"
                          data-testid={`edit-transaction-${t.id}`}
                          title="تعديل"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteTransaction(t.id)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                          data-testid={`delete-transaction-${t.id}`}
                          title="حذف"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      </>)}

      {/* Add/Edit Transaction Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingTransaction(null); }}>
          <div className="modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingTransaction ? "تعديل معاملة مالية" : "إضافة معاملة مالية"}</h2>
              <button onClick={() => { setShowModal(false); setEditingTransaction(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); editingTransaction ? updateTransaction() : handleSubmit(e); }}>
              <div className="modal-body">
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">نوع المعاملة *</label>
                    <select
                      className="form-input"
                      value={formData.transaction_type}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        transaction_type: e.target.value,
                        category: e.target.value === "income" ? "عملية" : "رواتب"
                      })}
                      data-testid="transaction-type-select"
                    >
                      <option value="income">إيراد</option>
                      <option value="expense">مصروف</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">الفئة *</label>
                    <select
                      className="form-input"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value, custom_category: "" })}
                      data-testid="category-select"
                    >
                      {(formData.transaction_type === "income" ? incomeCategories : expenseCategories).map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {formData.category === "أخرى" && (
                  <div className="form-group">
                    <label className="form-label">تحديد الفئة *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.custom_category}
                      onChange={(e) => setFormData({ ...formData, custom_category: e.target.value })}
                      placeholder="اكتب نوع الإيراد/المصروف"
                      required
                      data-testid="custom-category-input"
                    />
                  </div>
                )}
                
                <div className="form-group">
                  <label className="form-label">المريض (اختياري)</label>
                  <select
                    className="form-input"
                    value={formData.patient_id}
                    onChange={(e) => setFormData({ ...formData, patient_id: e.target.value })}
                    data-testid="patient-select"
                  >
                    <option value="">-- اختر مريض --</option>
                    {patients.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} {p.last_name || ""}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">المبلغ *</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      required
                      placeholder="0.00"
                      data-testid="amount-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">طريقة الدفع</label>
                    <select
                      className="form-input"
                      value={formData.payment_method}
                      onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                      data-testid="payment-method-select"
                    >
                      <option value="نقدي">نقدي</option>
                      <option value="بطاقة">بطاقة</option>
                      <option value="تحويل">تحويل بنكي</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">التاريخ *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    data-testid="date-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">الوصف</label>
                  <textarea
                    className="form-input"
                    rows={2}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="ملاحظات إضافية..."
                    data-testid="description-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">التفاصيل (شنو صرفت/شنو الغرض)</label>
                  <textarea
                    className="form-input"
                    rows={2}
                    value={formData.details}
                    onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                    placeholder="مثال: مواد تنظيف + قفازات + أكياس..."
                    data-testid="details-input"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn btn-primary" data-testid="save-transaction-btn">
                  حفظ المعاملة
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false, adminOrAccountant = false, requiredPerm = null }) => {
  const { user, loading, isAdmin, isAdminOrAccountant, permissions } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse-soft text-gray-500">جاري التحميل...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Permission-based check (preferred)
  if (requiredPerm && !permissions[requiredPerm]) {
    return <Navigate to="/dashboard" replace />;
  }

  // Legacy role-based checks (fallback)
  if (adminOnly && !isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (adminOrAccountant && !isAdminOrAccountant()) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Layout Component
const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="app-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main className="main-content">
        {/* Mobile top bar */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-3 py-2 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center">
              <Activity className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-sm text-gray-800">المركز الوطني</span>
          </div>
          <button
            className="p-2 bg-gray-100 rounded-lg"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
        {children}
      </main>
    </div>
  );
};

// Main App
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors dir="rtl" />
        <Routes>
          <Route path="/" element={<WebsitePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<PasswordResetPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/patients"
        element={
          <ProtectedRoute>
            <Layout>
              <PatientsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/patients/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <PatientDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounting"
        element={
          <ProtectedRoute requiredPerm="accounting">
            <Layout>
              <AccountingPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/debts"
        element={
          <ProtectedRoute requiredPerm="debts">
            <Layout>
              <DebtsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments"
        element={
          <ProtectedRoute>
            <Layout>
              <AppointmentsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sessions"
        element={
          <ProtectedRoute>
            <Layout>
              <SessionsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <Layout>
              <InventoryPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Layout>
              <ReportsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Layout>
              <NotificationsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/followup"
        element={
          <ProtectedRoute>
            <Layout>
              <FollowUpPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employees/:id"
        element={
          <ProtectedRoute requiredPerm="payroll">
            <Layout>
              <EmployeeDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/consultations"
        element={
          <ProtectedRoute>
            <Layout>
              <ConsultationsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff-stats"
        element={
          <ProtectedRoute>
            <Layout>
              <StaffStatsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute requiredPerm="settings">
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/website-manage"
        element={
          <ProtectedRoute requiredPerm="website">
            <Layout>
              <WebsiteManagePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-dashboard"
        element={
          <ProtectedRoute requiredPerm="ai">
            <Layout>
              <AIDashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads"
        element={
          <ProtectedRoute requiredPerm="website">
            <Layout>
              <LeadsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/offers-reviews"
        element={
          <ProtectedRoute requiredPerm="website">
            <Layout>
              <OffersReviewsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/review" element={<ReviewPage />} />
      <Route
        path="/payroll"
        element={
          <ProtectedRoute requiredPerm="payroll">
            <Layout>
              <PayrollPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/instagram-manage"
        element={
          <ProtectedRoute requiredPerm="website">
            <Layout>
              <InstagramManagePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-chat"
        element={
          <ProtectedRoute requiredPerm="ai">
            <Layout>
              <AIChatPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-features"
        element={
          <ProtectedRoute requiredPerm="ai">
            <Layout>
              <AIFeaturesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/advanced-accounting"
        element={
          <ProtectedRoute requiredPerm="accounting">
            <Layout>
              <AdvancedAccountingPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
